import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import { checkUserExists, loadUserGarages, loadSharedGarages, loadUserVehicles, loadGarageVehicles, updateGarageInSupabase, updateVehicleInSupabase, deleteVehicleFromSupabase, vehicleHasPhotoInSupabase, garageHasPhotoInSupabase, backfillVehicleGarageIds, pushVehicleToSupabase, createVehicle } from './services/userService';
import { getUserPermission } from './services/permissionService';
import { saveServiceLog, saveMileageLog, updateVehicleMileage, loadServiceLogs, loadVehicleServiceLogs, mapServiceLogToLocal } from './services/serviceLogService';
import { loadUserAnalyses, loadUserValueHistory, loadVehicleAnalysis, loadVehicleValueHistory, saveAnalysisToSupabase, saveValueSnapshotToSupabase, updateMaintenanceInSupabase, updateValuationInSupabase } from './services/analysisService';
import { saveReminderToSupabase, deleteReminderFromSupabase, loadUserReminders, loadVehicleReminders, mapReminderToLocal } from './services/reminderService';
import { updateUserProfile } from './services/userService';
import { reliableWrite, processQueue, getPendingWriteCount } from './services/writeQueue';
import { isNativeApp } from './utils/platform';
import { requestPushToken, registerPushToken } from './utils/pushNotifications';
import {
  getPendingInvitationCount as fetchInviteCount,
  getPendingInvitations as fetchInvites,
  acceptInvitation as acceptInvite,
  declineInvitation as declineInvite,
} from './services/invitationService';
import { migrateIfNeeded } from './utils/migration';
import { checkConnectStatus } from './services/stripeService';
import { populateDemoData, populateBusinessDemoData, populateClubDemoData } from './utils/demoData';
import CollectContact from './components/CollectContact';
import CollectName from './components/CollectName';
import Onboarding from './components/onboarding/Onboarding';
import Processing from './components/Processing';
import Dashboard from './components/dashboard/Dashboard';
import GarageList from './components/garage/GarageList';
import VehicleList from './components/garage/VehicleList';
import VehicleDetail from './components/vehicle/VehicleDetail';
import Chat from './components/chat/Chat';
import AutoAdvisor from './components/auto-advisor/AutoAdvisor';
import BottomNav from './components/BottomNav';
import Settings from './components/settings/Settings';
import Account from './components/Account';
import Toast from './components/Toast';
import UpgradeModal from './components/UpgradeModal';
import PullToRefresh from './components/PullToRefresh';
import {
  getGarages,
  getVehicles,
  getAllAnalyses,
  getServiceHistory,
  addServiceEntry as storageAddServiceEntry,
  removeServiceEntry as storageRemoveServiceEntry,
  updateServiceEntry as storageUpdateServiceEntry,
  addGarage as storageAddGarage,
  updateGarage as storageUpdateGarage,
  removeGarage as storageRemoveGarage,
  saveVehicle as storageSaveVehicle,
  removeVehicle as storageRemoveVehicle,
  moveVehicle as storageMoveVehicle,
  clearAll,
  getSettings,
  getSubscriptionLimits,
  getOwnedVehicleCount,
  updateSettings,
  upsertGarage,
  saveUserProfile,
  saveGarages,
  saveVehicles,
  hasUserProfile,
  syncSubscriptionFromSupabase,
  rekeyAnalysis,
  saveServiceHistory,
  saveAnalysis as storageSaveAnalysis,
  getReminders,
  addReminder as storageAddReminder,
  updateReminder as storageUpdateReminder,
  completeReminder as storageCompleteReminder,
  removeReminder as storageRemoveReminder,
  saveReminders,
  setBackupUserId,
} from './utils/storage';
import { loadAllBackupData, syncAllBackupData, syncBackupDataType } from './services/backupService';
import { getUserClubs as fetchUserClubs, joinClub, joinPlatformClub } from './services/clubService';
import { getUserTodayCheckinEvents, getPublicEventDetail, isBlacklisted } from './services/eventService';
import { fetchUnreadAnnouncement } from './services/adminService';
import { getClubMemberships, saveClubMemberships } from './utils/storage';

// Auto-reload once on stale chunk errors (after deploys, browsers may cache old index.html)
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      throw err; // second failure = real error, let ErrorBoundary handle
    })
  );
}

const AdminDashboard = lazyRetry(() => import('./components/admin/AdminDashboard'));
const SuperAdmin = lazyRetry(() => import('./components/admin/SuperAdmin'));
const AnnouncementViewer = lazyRetry(() => import('./components/AnnouncementViewer'));
const ClubAdmin = lazyRetry(() => import('./components/clubs/ClubAdmin'));
const ClubPricing = lazyRetry(() => import('./components/clubs/ClubPricing'));
const JoinClub = lazyRetry(() => import('./components/events/JoinClub'));
const EventsTab = lazyRetry(() => import('./components/events/EventsTab'));
const EventDetail = lazyRetry(() => import('./components/events/EventDetail'));
import PublicEventPreview from './components/events/PublicEventPreview';
import Referrals from './components/Referrals';
const SetupProfileLanding = lazyRetry(() => import('./components/SetupProfileLanding'));
const RouteBuilder = lazyRetry(() => import('./components/routes/RouteBuilder'));
const RouteList = lazyRetry(() => import('./components/routes/RouteList'));
const RouteViewer = lazyRetry(() => import('./components/routes/RouteViewer'));
const RoutesLanding = lazyRetry(() => import('./components/routes/RoutesLanding'));
const FunRouteExplorer = lazyRetry(() => import('./components/fun-routes/FunRouteExplorer'));
import { acceptReferral, notifyReferralAccepted, getBonusVehicles } from './services/referralService';
const ADMIN_EMAIL = 'carsten@propermiles.com';

// Obfuscated demo email detection — hashes keep demo addresses out of the bundle
function _demoH(s) {
  if (!s) return '';
  let x = 5381;
  for (let i = 0; i < s.length; i++) x = ((x << 5) + x) ^ s.charCodeAt(i);
  return (x >>> 0).toString(36);
}
// Hashes for demo@, business@, club@ (in order)
const _DH_ALEX = '1kkwi4d';
const _DH_BIZ = '1sxm2zc';
const _DH_CLUB = 'lzveiu';
function _demoVariantFromEmail(e) {
  const h = _demoH((e || '').toLowerCase());
  if (h === _DH_ALEX) return 'alex';
  if (h === _DH_BIZ) return 'business';
  if (h === _DH_CLUB) return 'club';
  return null;
}

const SCREENS = {
  ONBOARDING: 'onboarding',
  PROCESSING: 'processing',
  DASHBOARD: 'dashboard',
  GARAGE_LIST: 'garage_list',
  VEHICLE_LIST: 'vehicle_list',
  DETAIL: 'detail',
  CHAT: 'chat',
  AUTO_ADVISOR: 'auto_advisor',
  SETTINGS: 'settings',
  ACCOUNT: 'account',
  ADMIN: 'admin',
  CLUB_ADMIN: 'club_admin',
  EVENTS: 'events',
  EVENT_DETAIL: 'event_detail',
  JOIN_CLUB: 'join_club',
  REFERRALS: 'referrals',
  CLUB_PRICING: 'club_pricing',
  SETUP_PROFILE: 'setup_profile',
  COLLECT_CONTACT: 'collect_contact',
  ROUTE_LIST: 'route_list',
  ROUTE_BUILDER: 'route_builder',
  ROUTE_VIEWER: 'route_viewer',
  ROUTES: 'routes',
  FUN_ROUTES: 'fun_routes',
  SUPER_ADMIN: 'super_admin',
  ANNOUNCEMENT: 'announcement',
};

const NAV_SCREENS = [
  SCREENS.DASHBOARD,
  SCREENS.GARAGE_LIST,
  SCREENS.VEHICLE_LIST,
  SCREENS.DETAIL,
  SCREENS.AUTO_ADVISOR,
  SCREENS.EVENTS,
  SCREENS.EVENT_DETAIL,
  SCREENS.ROUTES,
  SCREENS.ROUTE_BUILDER,
  SCREENS.CLUB_ADMIN,
  SCREENS.SETTINGS,
  SCREENS.SETUP_PROFILE,
];

function App() {
  // ── Route Viewer URL detection ──
  // If the URL matches /r/<slug>, render the RouteViewer full-screen
  // (no auth required — public route viewing with optional guest join for tracking)
  const routeSlugMatch = window.location.pathname.match(/^\/r\/([^/]+)/);
  if (routeSlugMatch) {
    const routeSlug = routeSlugMatch[1];
    return (
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh', background: '#1a1a1a', color: '#a3a3a3' }}>
          Loading route...
        </div>
      }>
        <div className="route-fullscreen">
          <RouteViewer
            slug={routeSlug}
            onBack={() => { window.location.href = '/'; }}
          />
        </div>
      </Suspense>
    );
  }

  const { user, loading: authLoading, isDemoMode, demoVariant } = useAuth();

  const [screen, setScreen] = useState(SCREENS.ONBOARDING);
  const [garages, setGarages] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [analyses, setAnalyses] = useState({});
  const [serviceHistory, setServiceHistory] = useState([]);
  const [reminders, setReminders] = useState([]);

  // Toast state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Navigation context
  const [selectedGarageId, setSelectedGarageId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  // Auto advisor mode state for launching directly into purchase or repair mode
  const [autoAdvisorInitMode, setAutoAdvisorInitMode] = useState(null);

  // Chat initial message — for "Ask Pocket Mechanic" from DIY guide steps
  const [chatInitialMessage, setChatInitialMessage] = useState(null);

  // Profile loading — prevents flash of onboarding while checking Supabase
  const [profileLoading, setProfileLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Checking your account…');

  // Pending invitations
  const [pendingInvitationCount, setPendingInvitationCount] = useState(0);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  // Receipt photos from onboarding — held in React state (not localStorage) for Processing
  const [pendingReceiptPhotos, setPendingReceiptPhotos] = useState([]);

  // Contact collection — shown once when user is missing email or phone
  const [needsContact, setNeedsContact] = useState(null); // 'email' | 'phone' | null
  // Name collection — shown once for partial users (event/route signups) missing first_name
  const [needsName, setNeedsName] = useState(false);

  // Clubs & Events
  const [isPartialUser, setIsPartialUser] = useState(false);
  const [userClubs, setUserClubs] = useState([]);
  const [isClubOwner, setIsClubOwner] = useState(false);
  const [clubOwnerTier, setClubOwnerTier] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isEventsBlacklisted, setIsEventsBlacklisted] = useState(false);
  const [selectedRouteForEdit, setSelectedRouteForEdit] = useState(null);
  const [routeBuilderReturnScreen, setRouteBuilderReturnScreen] = useState(null);
  const [routeSlugForViewer, setRouteSlugForViewer] = useState(null);
  const [routeViewerReturnScreen, setRouteViewerReturnScreen] = useState(null);
  const [todayCheckinEvents, setTodayCheckinEvents] = useState([]);

  // Account screen intent from URL params (for native app → browser flow)
  // Lazy-initialized so accountIntent is true from the very first render
  const [accountIntent] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('screen') === 'account';
  });
  const [adminIntent] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('screen') === 'admin';
  });
  const [initialEmail] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('email') || '';
  });
  const [joinClubSlug] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const fromParam = p.get('join');
    if (fromParam) return fromParam;
    const pathMatch = window.location.pathname.match(/^\/join\/([a-z0-9-]+)$/i);
    return pathMatch ? pathMatch[1] : '';
  });
  // Persisted for the tab session: Google/Apple/Facebook sign-in redirects
  // back to the bare origin, and the URL is cleaned to '/' on mount, so
  // without this a new user signing in from an event link loses the event
  // and lands in onboarding instead.
  const [eventIntent] = useState(() => {
    const pathMatch = window.location.pathname.match(/^\/event\/([a-f0-9-]+)$/i);
    if (pathMatch) {
      try { sessionStorage.setItem('pendingEventIntent', pathMatch[1]); } catch { /* no-op */ }
      return pathMatch[1];
    }
    try { return sessionStorage.getItem('pendingEventIntent') || ''; } catch { return ''; }
  });
  const [referralCode] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const code = p.get('ref') || '';
    if (code) sessionStorage.setItem('pendingReferral', code);
    return code || sessionStorage.getItem('pendingReferral') || '';
  });
  const [funRouteIntent] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('fun') === '1';
  });
  const [announcementIntent, setAnnouncementIntent] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('screen') === 'announcement' ? p.get('id') : '';
  });
  // Deep link to Club Admin (?screen=club). Lets an invited admin reach the
  // Club Admin screen directly even if the nav tab isn't showing yet.
  const [clubAdminIntent] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('screen') === 'club';
  });
  const [eventLoginEmail, setEventLoginEmail] = useState('');

  // Once signed in, the event intent has been consumed for this mount —
  // drop it so a later reload doesn't keep reopening the event.
  useEffect(() => {
    if (user && eventIntent) {
      try { sessionStorage.removeItem('pendingEventIntent'); } catch { /* no-op */ }
    }
  }, [user, eventIntent]);

  // Clean URL params after reading
  useEffect(() => {
    if (accountIntent || adminIntent || joinClubSlug || eventIntent || referralCode || funRouteIntent || announcementIntent || clubAdminIntent) {
      window.history.replaceState({}, '', '/');
    }
    // Clear stale-chunk reload flag on successful app load
    sessionStorage.removeItem('chunk_reload');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scroll to top on every screen change. On mobile the window itself
  // scrolls, and the WKWebView drifts its scroll position during async content
  // load (same reason RouteViewer force-scrolls) — most visible landing on the
  // dashboard right after login. Re-fire after a beat to catch async reflow.
  useEffect(() => {
    const toTop = () => {
      try {
        window.scrollTo(0, 0);
        document.querySelector('.app-content')?.scrollTo?.(0, 0);
      } catch { /* no-op */ }
    };
    toTop();
    const t1 = setTimeout(toTop, 150);
    const t2 = setTimeout(toTop, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [screen]);

  // Derived values
  const selectedGarage = garages.find((g) => g.id === selectedGarageId) || null;
  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId) || null;
  const selectedAnalysis = selectedVehicleId ? analyses[selectedVehicleId] || null : null;
  const selectedServiceHistory = serviceHistory.filter(
    (e) => e.vehicleId === selectedVehicleId
  );

  // Club Admin access: granted to club owners (is_club_owner flag) AND to
  // users who were added as an admin/owner of someone else's club. The
  // is_club_owner flag is only set when a user owns/creates a club, so
  // gating purely on it locked out invited admins (e.g. a club president
  // added by the club's owner) even though they have a valid admin
  // membership. userClubs carries each membership's user_role.
  const canManageClubs = isClubOwner
    || userClubs.some(c => c.user_role === 'admin' || c.user_role === 'owner');

  // Honor the ?screen=club deep link once club memberships have loaded and
  // access is confirmed (clubs load async, so the initial screen resolution
  // may run before userClubs is populated). Consume the intent once so we
  // don't fight the user if they navigate elsewhere.
  const clubIntentConsumedRef = useRef(false);
  useEffect(() => {
    if (clubAdminIntent && !clubIntentConsumedRef.current && canManageClubs) {
      clubIntentConsumedRef.current = true;
      setScreen(SCREENS.CLUB_ADMIN);
    }
  }, [clubAdminIntent, canManageClubs]);

  // Reload all data from storage
  function reloadAll() {
    setGarages(getGarages());
    setVehicles(getVehicles());
    setAnalyses(getAllAnalyses());
    setServiceHistory(getServiceHistory());
    setReminders(getReminders());
  }

  // Determine the correct initial screen based on URL intents
  function getInitialScreen() {
    // Demo accounts ignore announcement deep links — reviewers and demo
    // users would otherwise land on "announcement not found" if a stale
    // push URL is still in the address bar / referrer.
    const detectedVariant = _demoVariantFromEmail(user?.email);
    const inDemo = isDemoMode || !!detectedVariant;
    if (announcementIntent && !inDemo) return SCREENS.ANNOUNCEMENT;
    if (funRouteIntent) return SCREENS.FUN_ROUTES;
    if (adminIntent && user?.email === ADMIN_EMAIL) return SCREENS.ADMIN;
    if (clubAdminIntent && canManageClubs) return SCREENS.CLUB_ADMIN;
    if (accountIntent) return SCREENS.ACCOUNT;
    if (joinClubSlug) return SCREENS.JOIN_CLUB;
    if (eventIntent) {
      // Set the event ID and route to event detail after auth
      setSelectedEventId(eventIntent);
      return SCREENS.EVENT_DETAIL;
    }
    return SCREENS.DASHBOARD;
  }

  // Load user's club memberships from Supabase (+ bootstrap from localStorage cache)
  async function loadUserClubs() {
    if (!user?.id || (isDemoMode && demoVariant !== 'club')) { setUserClubs([]); return; }
    try {
      const clubs = await fetchUserClubs(user.id);
      setUserClubs(clubs);
      saveClubMemberships(clubs);
    } catch {
      // Fall back to cached
      setUserClubs(getClubMemberships());
    }
  }

  // Load today's events where user is registered but not checked in (for Dashboard banner)
  async function loadTodayCheckinEvents() {
    if (!user?.id || isDemoMode) { setTodayCheckinEvents([]); return; }
    try {
      const events = await getUserTodayCheckinEvents(user.id);
      setTodayCheckinEvents(events);
    } catch {
      setTodayCheckinEvents([]);
    }
  }

  useEffect(() => {
    migrateIfNeeded();
    const g = getGarages();
    const v = getVehicles();
    const a = getAllAnalyses();
    const sh = getServiceHistory();
    setGarages(g);
    setVehicles(v);
    setAnalyses(a);
    setServiceHistory(sh);
    setReminders(getReminders());

    if (v.length > 0) {
      setScreen(getInitialScreen());
      // Local data already present — no Supabase check needed, skip loading
      setProfileLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if authenticated user has a Supabase profile & hydrate localStorage
  // Uses user?.id as dep (not `user`) so token refreshes don't re-trigger hydration
  const userId = user?.id;
  const userEmail = user?.email;
  useEffect(() => {
    if (!userId) {
      console.log('[Auth] No user — skipping profile check');
      setProfileLoading(false);
      return;
    }

    // Detect account switch: if a different user logs in, wipe stale
    // localStorage so the old account's data never bleeds through.
    const lastUserId = localStorage.getItem('pm_last_user_id');
    if (lastUserId && lastUserId !== userId) {
      console.log('[Auth] Account switch detected — clearing stale cache');
      clearAll();
      localStorage.removeItem('pm_active_navigation');
    }
    localStorage.setItem('pm_last_user_id', userId);

    // Demo mode: populate localStorage with demo data, skip all Supabase calls
    // Check email directly (not just isDemoMode) to avoid race condition where
    // isDemoMode hasn't been set yet when onAuthStateChange triggers this effect.
    const detectedVariant = _demoVariantFromEmail(userEmail);
    if (isDemoMode || detectedVariant) {
      if (detectedVariant === 'business') {
        populateBusinessDemoData();
      } else if (detectedVariant === 'club') {
        populateClubDemoData();
        // Club demo uses real Supabase data for clubs
        setIsClubOwner(true);
        setClubOwnerTier('club_starter');
        // Load clubs from Supabase (non-blocking)
        fetchUserClubs(userId).then(clubs => {
          setUserClubs(clubs);
          saveClubMemberships(clubs);
        }).catch(() => {});
      } else {
        populateDemoData();
      }
      reloadAll();
      setScreen(getInitialScreen());
      setProfileLoading(false);
      return;
    }

    let cancelled = false;

    // Start loading (unless already resolved by mount effect with local data)
    const localV = getVehicles();
    if (localV.length > 0) {
      // Already resolved by mount effect — skip loading gate
      setProfileLoading(false);
      // Background sync: align localStorage garage IDs with Supabase UUIDs,
      // sync garage name/location, and backfill vehicle garage_id.
      setBackupUserId(userId);

      // Auto-join the event's club when arriving via event link (non-blocking)
      if (eventIntent) {
        getPublicEventDetail(eventIntent).then(ev => {
          if (ev?.club_id && userId) {
            joinClub(ev.club_id, userId, 'event_link').then(() => loadUserClubs());
          }
        }).catch(() => {});
      }

      (async () => {
        try {
          // Sync subscription
          const profile = await checkUserExists(userId);
          if (profile) {
            syncSubscriptionFromSupabase(profile);
            setIsClubOwner(!!profile.is_club_owner);
            setClubOwnerTier(profile.club_owner_tier || null);
            loadUserClubs();
            loadTodayCheckinEvents();

            // Auto-join platform club so Events tab has content (non-blocking)
            joinPlatformClub(userId).then(() => loadUserClubs()).catch(() => {});

            // Check blacklist for nav hiding (non-blocking)
            if (userEmail) isBlacklisted(userEmail).then(bl => setIsEventsBlacklisted(bl)).catch(() => {});

            // Check if user is missing email or phone — prompt to collect once
            if (profile.is_onboarded) {
              if (!profile.phone_number) {
                setNeedsContact('phone');
              } else if (!profile.email) {
                setNeedsContact('email');
              }
            }
          }

          // Check if any local garages have localStorage IDs that need replacing
          let allGarages = getGarages();
          let allVehicles = getVehicles();
          const localIdGarages = allGarages.filter(
            (g) => g.role !== 'collaborator' && String(g.id).startsWith('garage_')
          );

          if (localIdGarages.length > 0) {
            // Fetch Supabase garages to find the real UUIDs
            const sbGarages = await loadUserGarages(userId);
            if (sbGarages.length > 0) {
              for (const localG of localIdGarages) {
                // Match by position (first local → first supabase) since names may differ
                const sbMatch = sbGarages.find(
                  (sg) => !allGarages.some((lg) => lg.id === sg.id && lg.role !== 'collaborator')
                ) || sbGarages[0];

                if (sbMatch) {
                  const oldId = localG.id;
                  const newId = sbMatch.id;
                  // Update garage ID
                  localG.id = newId;
                  // Update vehicle references
                  allVehicles.forEach((v) => {
                    if (v.garageId === oldId) v.garageId = newId;
                  });
                  // Track selected IDs so navigation stays on current screen
                  if (selectedGarageId === oldId) setSelectedGarageId(newId);
                  console.log('[PM-Sync] Replaced garage ID:', oldId, '→', newId);
                }
              }
              saveGarages(allGarages);
              saveVehicles(allVehicles);
              reloadAll();
            }
          }

          // Now sync garage names + backfill vehicle garage_id
          allGarages = getGarages();
          allVehicles = getVehicles();
          for (const g of allGarages) {
            if (g.role === 'collaborator' || String(g.id).startsWith('garage_')) continue;
            await updateGarageInSupabase(g.id, { name: g.name, location: g.location || '' });
            const vids = allVehicles.filter((v) => v.garageId === g.id).map((v) => v.id);
            if (vids.length > 0) await backfillVehicleGarageIds(g.id, vids);
          }

          // Push any vehicles that exist locally but NOT in Supabase
          await pushUnsyncedVehicles(userId);

          // Push any analyses that exist locally but NOT in Supabase
          await pushUnsyncedAnalyses(userId);

          // Push any vehicle photos that exist locally but NOT in Supabase
          await pushUnsyncedPhotos(userId);

          // Push any garage photos that exist locally but NOT in Supabase
          await pushUnsyncedGaragePhotos(userId);

          // Hydrate reminders from Supabase (pulls in reminders created on other devices)
          try {
            const sbReminders = await loadUserReminders(userId);
            if (!cancelled && sbReminders.length > 0) {
              const localReminders = getReminders();
              const localIds = new Set(localReminders.map((r) => r.id));
              let merged = [...localReminders];
              let added = 0;
              for (const row of sbReminders) {
                const local = mapReminderToLocal(row);
                if (!localIds.has(local.id)) {
                  merged.push(local);
                  added++;
                }
              }
              // Also sync status changes (e.g. completed on another device)
              if (added === 0) {
                const sbMap = new Map(sbReminders.map(r => [r.id, r]));
                merged = localReminders.map(lr => {
                  const sb = sbMap.get(lr.id);
                  if (sb && sb.status !== lr.status) {
                    return { ...lr, status: sb.status, completedAt: sb.completed_at || lr.completedAt };
                  }
                  return lr;
                });
              }
              saveReminders(merged);
              setReminders(merged);
              if (added > 0) console.log('[PM-Sync] Hydrated', added, 'reminders from Supabase');
            }
          } catch (err) {
            console.error('[PM-Sync] Reminders sync failed:', err.message);
          }

          // Process any queued writes that failed previously
          await processWriteQueue();

          // Sync all backup data types to Supabase (paid users only)
          await syncAllBackupData(userId);
        } catch (err) {
          console.error('[PM-Sync] Background sync error:', err);
        }
      })();
      return;
    }

    setProfileLoading(true);
    setLoadingMessage('Checking your account…');

    // Temporary production-visible logging to debug onboarding loop
    const _log = (...args) => console.log('[PM-Auth]', ...args);
    const _err = (...args) => console.error('[PM-Auth]', ...args);

    _log('Auth state changed — user:', userId, userEmail);

    (async () => {
      try {
        _log('Calling checkUserExists for:', userId);
        const profile = await checkUserExists(userId);
        if (cancelled) return;
        _log('checkUserExists returned:', profile ? 'FOUND' : 'NOT FOUND', profile ? { tier: profile.subscription_tier, credits: profile.credits_remaining, email: profile.email } : null);

        if (profile) {
          // Fire GA4 sign_up conversion for brand-new users (auto-created by trigger but never onboarded)
          if (!profile.is_onboarded && typeof window.gtag === 'function') {
            _log('Firing GA4 sign_up event (new user, is_onboarded=false)');
            window.gtag('event', 'sign_up', { method: user?.email ? 'email' : 'phone' });
          }

          // Sync subscription tier + credits from Supabase → localStorage
          syncSubscriptionFromSupabase(profile);
          setBackupUserId(userId);
          setIsClubOwner(!!profile.is_club_owner);
          setClubOwnerTier(profile.club_owner_tier || null);
          _log('Synced subscription:', profile.subscription_tier, 'credits:', profile.credits_remaining);

          // Load club memberships + today's check-in events (non-blocking)
          loadUserClubs();
          loadTodayCheckinEvents();

          // Auto-join platform club so Events tab has content (non-blocking)
          joinPlatformClub(userId).then(() => loadUserClubs()).catch(() => {});

          // Check blacklist for nav hiding (non-blocking)
          if (userEmail) isBlacklisted(userEmail).then(bl => setIsEventsBlacklisted(bl)).catch(() => {});

          // Capture push notification token (native app only, non-blocking)
          // Runs on every auth session restore to ensure token stays current
          // (tokens can change after app rebuilds or OS updates)
          requestPushToken().then(token => {
            if (token && userId) {
              registerPushToken(userId, token).catch(() => {});
            }
          });

          // Check for unread announcements and show if found (non-blocking)
          if (!announcementIntent) {
            fetchUnreadAnnouncement().then(annId => {
              if (annId) {
                setAnnouncementIntent(annId);
                setScreen(SCREENS.ANNOUNCEMENT);
              }
            }).catch(() => {});
          }

          // Accept referral if ?ref=CODE was in the URL (non-blocking)
          if (referralCode) {
            acceptReferral(referralCode).then(({ referrerId }) => {
              sessionStorage.removeItem('pendingReferral');
              if (referrerId) {
                notifyReferralAccepted(referrerId);
                _log('Referral accepted, notified referrer:', referrerId);
              }
            }).catch(() => {});
          }

          // Existing user — check if localStorage already has their data
          const localVehicles = getVehicles();
          const localGarages = getGarages();
          const localHasProfile = hasUserProfile();

          _log('Local state:', {
            hasProfile: localHasProfile,
            garages: localGarages.length,
            vehicles: localVehicles.length,
            analyses: Object.keys(getAllAnalyses()).length,
          });

          if (localVehicles.length > 0) {
            // Already have local data — go to dashboard (or account if intent set)
            _log(accountIntent ? '→ ACCOUNT (local data present, account intent)' : '→ DASHBOARD (local data present)');
            setScreen(getInitialScreen());

            // Check if user is missing email or phone — prompt to collect once
            if (profile.is_onboarded) {
              if (!profile.phone_number) {
                setNeedsContact('phone');
              } else if (!profile.email) {
                setNeedsContact('email');
              }
            }
          } else {
            // Returning user with empty localStorage — hydrate from Supabase
            if (!cancelled) setLoadingMessage('Loading your garage…');
            _log('Hydrating localStorage from Supabase...');

            // Hydrate user profile
            if (!localHasProfile) {
              saveUserProfile({
                firstName: profile.first_name || '',
                lastName: profile.last_name || '',
                email: profile.email || userEmail,
                phone: profile.phone_number || '',
                zipCode: profile.zip_code || '',
                yearOfBirth: profile.year_of_birth || '',
                salaryRange: profile.annual_income || '',
                mechanicalLevel: profile.mechanical_level || '',
                primaryUseCases: profile.primary_use_cases || [],
                referralSource: profile.referral_source || '',
              });
              console.log('[Auth] Hydrated user profile');
            }

            // Load garages from Supabase
            const sbGarages = await loadUserGarages(userId);
            if (cancelled) return;
            _log('Supabase garages:', sbGarages.length, sbGarages.map(g => ({ id: g.id, name: g.name })));

            if (sbGarages.length > 0 && localGarages.length === 0) {
              const hydratedGarages = sbGarages.map((g) => ({
                id: g.id,
                name: g.name || 'My Garage',
                bannerPhoto: g.banner_photo || null,
                vehicleIds: [],
                location: g.location || '',
                owner: userEmail,
                collaborators: g.collaborators || [],
              }));
              saveGarages(hydratedGarages);
              _log('Hydrated garages:', hydratedGarages.length);
            }

            // Load vehicles from Supabase
            if (!cancelled) setLoadingMessage('Loading your vehicles…');
            const sbVehicles = await loadUserVehicles(userId);
            if (cancelled) return;
            _log('Supabase vehicles:', sbVehicles.length, sbVehicles.map(v => ({ id: v.id, make: v.make, model: v.model, user_id: v.user_id, garage_id: v.garage_id })));

            if (sbVehicles.length > 0 && localVehicles.length === 0) {
              // Deduplicate Supabase vehicles: if two share the same year+make and one model
              // is a prefix of the other, keep only the more complete (longer model or has photo).
              const deduped = [];
              const used = new Set();
              for (let i = 0; i < sbVehicles.length; i++) {
                if (used.has(i)) continue;
                let best = sbVehicles[i];
                for (let j = i + 1; j < sbVehicles.length; j++) {
                  if (used.has(j)) continue;
                  const other = sbVehicles[j];
                  if (String(best.year) === String(other.year) &&
                      (best.make || '').toLowerCase() === (other.make || '').toLowerCase()) {
                    const m1 = (best.model || '').toLowerCase().trim();
                    const m2 = (other.model || '').toLowerCase().trim();
                    if (m1 === m2 || m1.startsWith(m2) || m2.startsWith(m1)) {
                      // Pick the one with more data (longer model, or has photo)
                      const score1 = m1.length + (best.banner_photo ? 100 : 0);
                      const score2 = m2.length + (other.banner_photo ? 100 : 0);
                      if (score2 > score1) best = other;
                      used.add(j);
                      _log('[Dedup] Merging duplicate vehicles:', best.model, '/', other.model, '→ keeping', best.model);
                    }
                  }
                }
                deduped.push(best);
              }
              if (deduped.length < sbVehicles.length) {
                _log('[Dedup] Removed', sbVehicles.length - deduped.length, 'duplicate vehicle(s) from Supabase results');
              }

              const hydratedVehicles = deduped.map((v) => ({
                id: v.id,
                garageId: v.garage_id || (sbGarages.length > 0 ? sbGarages[0].id : null),
                year: v.year || '',
                make: v.make || '',
                model: v.model || '',
                trim: v.trim || '',
                mileage: v.current_mileage || '',
                tmu: v.is_tmu || false,
                condition: v.condition || '',
                primaryUse: v.primary_use || [],
                photo: v.banner_photo || null,
                lastMileageUpdateAt: v.last_mileage_update_at || null,
              }));
              saveVehicles(hydratedVehicles);

              // Re-key analyses: match local-ID analyses to Supabase UUIDs by vehicle fingerprint
              const currentAnalyses = getAllAnalyses();
              const localIdEntries = Object.entries(currentAnalyses)
                .filter(([key]) => key.startsWith('vehicle_'));
              if (localIdEntries.length > 0) {
                for (const [localId, analysisData] of localIdEntries) {
                  // Match by year+make+model in the analysis data itself
                  const match = hydratedVehicles.find((hv) =>
                    String(hv.year) === String(analysisData.year || '') &&
                    (hv.make || '').toLowerCase() === (analysisData.make || '').toLowerCase() &&
                    (hv.model || '').toLowerCase() === (analysisData.model || '').toLowerCase()
                  );
                  if (match) {
                    rekeyAnalysis(localId, match.id);
                    console.log('[Auth] Re-keyed analysis:', localId, '→', match.id);
                  }
                }
              }

              // Update garage vehicleIds
              const currentGarages = getGarages();
              hydratedVehicles.forEach((v) => {
                const g = currentGarages.find((g) => g.id === v.garageId);
                if (g && !g.vehicleIds.includes(v.id)) {
                  g.vehicleIds.push(v.id);
                }
              });
              saveGarages(currentGarages);

              // Backfill garage_id for any vehicles that have NULL garage_id in Supabase
              // (created while garage still had a localStorage ID)
              for (const g of currentGarages) {
                if (g.vehicleIds?.length > 0 && !String(g.id).startsWith('garage_')) {
                  backfillVehicleGarageIds(g.id, g.vehicleIds);
                }
              }

              _log('Hydrated vehicles:', hydratedVehicles.length);
            }

            // Hydrate analyses from Supabase
            if (!cancelled) setLoadingMessage('Restoring your analyses...');
            try {
              const sbAnalyses = await loadUserAnalyses(userId);
              if (!cancelled && Object.keys(sbAnalyses).length > 0) {
                const currentAnalyses = getAllAnalyses();
                let merged = 0;
                for (const [vid, analysis] of Object.entries(sbAnalyses)) {
                  if (!currentAnalyses[vid]) {
                    currentAnalyses[vid] = analysis;
                    merged++;
                  }
                }
                if (merged > 0) {
                  localStorage.setItem('pm_analyses', JSON.stringify(currentAnalyses));
                  console.log('[Auth] Hydrated', merged, 'analyses from Supabase');
                }
              }
            } catch (err) {
              console.error('[Auth] Analysis hydration failed:', err.message);
            }
            if (cancelled) return;

            // Hydrate service logs from Supabase
            try {
              const localLogs = getServiceHistory();
              if (localLogs.length === 0) {
                const sbLogs = await loadServiceLogs(userId);
                if (!cancelled && sbLogs.length > 0) {
                  const hydratedLogs = sbLogs
                    .filter((log) => log.service_type !== 'mileage_update')
                    .map(mapServiceLogToLocal);
                  saveServiceHistory(hydratedLogs);
                  console.log('[Auth] Hydrated', hydratedLogs.length, 'service logs from Supabase');
                }
              }
            } catch (err) {
              console.error('[Auth] Service log hydration failed:', err.message);
            }
            if (cancelled) return;

            // Hydrate value history from Supabase
            try {
              const sbValues = await loadUserValueHistory(userId);
              if (!cancelled && Object.keys(sbValues).length > 0) {
                const raw = localStorage.getItem('pm_value_history');
                const localValues = raw ? JSON.parse(raw) : {};
                let merged = false;
                for (const [vid, snapshots] of Object.entries(sbValues)) {
                  if (!localValues[vid] || localValues[vid].length === 0) {
                    localValues[vid] = snapshots;
                    merged = true;
                  }
                }
                if (merged) {
                  localStorage.setItem('pm_value_history', JSON.stringify(localValues));
                  console.log('[Auth] Hydrated value history for', Object.keys(sbValues).length, 'vehicles');
                }
              }
            } catch (err) {
              console.error('[Auth] Value history hydration failed:', err.message);
            }
            if (cancelled) return;

            // Hydrate reminders from Supabase
            try {
              const sbReminders = await loadUserReminders(userId);
              if (!cancelled && sbReminders.length > 0) {
                const localReminders = getReminders();
                const localIds = new Set(localReminders.map((r) => r.id));
                let merged = [...localReminders];
                let added = 0;
                for (const row of sbReminders) {
                  const local = mapReminderToLocal(row);
                  if (!localIds.has(local.id)) {
                    merged.push(local);
                    added++;
                  }
                }
                if (added > 0) {
                  saveReminders(merged);
                  setReminders(merged);
                  console.log('[Auth] Hydrated', added, 'reminders from Supabase');
                }
              }
            } catch (err) {
              console.error('[Auth] Reminders hydration failed:', err.message);
            }
            if (cancelled) return;

            // Hydrate localStorage-only data from cloud backup (paid users)
            try {
              const backupRows = await loadAllBackupData(userId);
              if (!cancelled && backupRows.length > 0) {
                const RESTORE_MAP = {
                  chat_histories: 'pm_chat_histories',
                  diy_guides: 'pm_diy_guides',
                  aa_analyses: 'pm_aa_analyses',
                  aa_checklist: 'pm_aa_checklist',
                  mileage_logs: 'pm_mileage_logs',
                  dismissed_items: 'pm_dismissed_items',
                  user_preferences: null, // special: merge into pm_settings.preferences
                };
                for (const row of backupRows) {
                  const lsKey = RESTORE_MAP[row.data_type];
                  if (lsKey) {
                    localStorage.setItem(lsKey, JSON.stringify(row.data));
                  } else if (row.data_type === 'user_preferences') {
                    updateSettings({ preferences: row.data });
                  }
                }
                _log('Restored', backupRows.length, 'backup data types from cloud');
              }
            } catch (err) {
              console.error('[Auth] Backup hydration failed:', err.message);
            }
            if (cancelled) return;

            // Hydrate shared garages (where user is a collaborator, not owner)
            try {
              if (!cancelled) setLoadingMessage('Checking shared garages…');
              const sbSharedGarages = await loadSharedGarages(userId);
              if (!cancelled && sbSharedGarages.length > 0) {
                _log('Shared garages from Supabase:', sbSharedGarages.length);
                for (const sg of sbSharedGarages) {
                  // Load vehicles for each shared garage
                  const sgVehicles = await loadGarageVehicles(sg.id);
                  if (cancelled) return;

                  const vehicleIds = [];
                  for (const v of sgVehicles) {
                    storageSaveVehicle({
                      id: v.id,
                      garageId: v.garage_id,
                      year: v.year || '',
                      make: v.make || '',
                      model: v.model || '',
                      trim: v.trim || '',
                      mileage: v.current_mileage || '',
                      tmu: v.is_tmu || false,
                      condition: v.condition || '',
                      primaryUse: v.primary_use || [],
                      photo: v.banner_photo || null,
                      lastMileageUpdateAt: v.last_mileage_update_at || null,
                    });
                    vehicleIds.push(v.id);
                  }

                  // Save the shared garage to localStorage
                  upsertGarage({
                    id: sg.id,
                    name: sg.name || 'Shared Garage',
                    bannerPhoto: sg.banner_photo || null,
                    vehicleIds,
                    location: sg.location || '',
                    owner: sg.owner_id || '',
                    collaborators: [],
                    role: 'collaborator',
                  });
                }
                _log('Hydrated', sbSharedGarages.length, 'shared garages');
              }
            } catch (err) {
              console.error('[PM-Auth] Shared garage hydration failed:', err.message);
            }
            if (cancelled) return;

            // Hydrate shared garage vehicle data (analyses, service logs, value history)
            // These use RPC calls that bypass RLS for vehicles the user collaborates on
            try {
              const currentGarages = getGarages();
              const sharedGarages = currentGarages.filter((g) => g.role === 'collaborator');
              if (sharedGarages.length > 0) {
                const currentAnalyses = getAllAnalyses();
                const existingLogs = getServiceHistory();
                const existingIds = new Set(existingLogs.map((e) => e.id));
                const rawValues = localStorage.getItem('pm_value_history');
                const localValues = rawValues ? JSON.parse(rawValues) : {};
                let analysisCount = 0;
                let logCount = 0;
                let reminderCount = 0;
                let newLogEntries = [];
                let sharedReminders = [];

                for (const garage of sharedGarages) {
                  for (const vid of (garage.vehicleIds || [])) {
                    if (cancelled) return;
                    // Analysis — always fetch from Supabase (owner may have updated since last session)
                    const analysis = await loadVehicleAnalysis(vid);
                    if (analysis) {
                      currentAnalyses[vid] = analysis;
                      analysisCount++;
                    }
                    // Service logs
                    const logs = await loadVehicleServiceLogs(vid);
                    if (logs.length > 0) {
                      const mapped = logs.map(mapServiceLogToLocal);
                      const toAdd = mapped.filter((e) => !existingIds.has(e.id));
                      newLogEntries.push(...toAdd);
                      toAdd.forEach((e) => existingIds.add(e.id));
                      logCount += toAdd.length;
                    }
                    // Value history — always fetch from Supabase
                    const valueHistory = await loadVehicleValueHistory(vid);
                    if (valueHistory.length > 0) {
                      localValues[vid] = valueHistory;
                    }
                    // Reminders — fetch from Supabase for shared vehicles
                    const vehicleReminders = await loadVehicleReminders(vid);
                    if (vehicleReminders.length > 0) {
                      sharedReminders.push(...vehicleReminders.map(mapReminderToLocal));
                      reminderCount += vehicleReminders.length;
                    }
                  }
                }

                if (analysisCount > 0) {
                  localStorage.setItem('pm_analyses', JSON.stringify(currentAnalyses));
                }
                if (newLogEntries.length > 0) {
                  saveServiceHistory([...newLogEntries, ...existingLogs]);
                }
                localStorage.setItem('pm_value_history', JSON.stringify(localValues));
                if (sharedReminders.length > 0) {
                  const allReminders = getReminders();
                  const existingReminderIds = new Set(allReminders.map((r) => r.id));
                  const toAddReminders = sharedReminders.filter((r) => !existingReminderIds.has(r.id));
                  if (toAddReminders.length > 0) {
                    saveReminders([...allReminders, ...toAddReminders]);
                    setReminders(getReminders());
                  }
                }

                console.log('[Auth] Hydrated shared garage data:', analysisCount, 'analyses,', logCount, 'service logs,', reminderCount, 'reminders');
              }
            } catch (err) {
              console.error('[Auth] Shared garage hydration failed:', err.message);
            }
            if (cancelled) return;

            // Validate collaborator access — remove revoked garages
            try {
              const latestGarages = getGarages();
              for (const g of latestGarages) {
                if (cancelled) return;
                if (g.role === 'collaborator' && g.id) {
                  const perm = await getUserPermission(g.id, userId, userEmail);
                  if (perm === null) {
                    console.log('[Auth] Collaborator revoked for garage:', g.id, '— removing');
                    storageRemoveGarage(g.id);
                  }
                }
              }
            } catch (err) {
              console.error('[Auth] Collaborator validation failed:', err.message);
            }
            if (cancelled) return;

            // Reload all state from localStorage
            reloadAll();

            // Decide screen: vehicles exist → dashboard, otherwise → onboarding
            const refreshedVehicles = getVehicles();
            _log('Post-hydration localStorage vehicles:', refreshedVehicles.length, refreshedVehicles.map(v => ({ id: v.id, make: v.make, model: v.model })));
            if (refreshedVehicles.length > 0) {
              _log(accountIntent ? '→ ACCOUNT (hydrated from Supabase)' : '→ DASHBOARD (hydrated from Supabase, vehicles:', refreshedVehicles.length, ')');
              setScreen(getInitialScreen());

              // Auto-join the event's club when arriving via event link (non-blocking)
              if (eventIntent) {
                getPublicEventDetail(eventIntent).then(ev => {
                  if (ev?.club_id && userId) {
                    joinClub(ev.club_id, userId, 'event_link').then(() => loadUserClubs());
                  }
                }).catch(() => {});
              }

              // Check if user is missing email or phone — prompt to collect once
              if (profile.is_onboarded) {
                if (!profile.phone_number) {
                  setNeedsContact('phone');
                } else if (!profile.email) {
                  setNeedsContact('email');
                }
              }
            } else {
              // User has Supabase profile but NO vehicles — their original onboarding
              // likely failed to save the vehicle to Supabase. Clear the hydrated data
              // so Onboarding runs as first-time (creates profile + garage + vehicle in Supabase).
              if (adminIntent && user?.email === ADMIN_EMAIL) {
                _log('→ ADMIN (existing user, no vehicles, admin intent)');
                setScreen(SCREENS.ADMIN);
              } else if (accountIntent) {
                _log('→ ACCOUNT (existing user, no vehicles, account intent)');
                setScreen(SCREENS.ACCOUNT);
              } else if (joinClubSlug) {
                _log('→ JOIN_CLUB (existing user, no vehicles, join intent)');
                setScreen(SCREENS.JOIN_CLUB);
              } else if (eventIntent) {
                _log('→ EVENT_DETAIL (existing user, no vehicles, event intent)');
                setIsPartialUser(true);
                if (!profile.first_name) setNeedsName(true);
                setSelectedEventId(eventIntent);
                setScreen(SCREENS.EVENT_DETAIL);
                // Auto-join the event's club (non-blocking)
                getPublicEventDetail(eventIntent).then(ev => {
                  if (ev?.club_id && userId) {
                    joinClub(ev.club_id, userId, 'event_link').then(() => loadUserClubs());
                  }
                });
              } else {
                // Check if user has club memberships (partial user returning)
                const clubs = await fetchUserClubs(userId);
                if (clubs && clubs.length > 0) {
                  _log('→ SETUP_PROFILE (existing user, no vehicles, has club memberships — partial user)');
                  setIsPartialUser(true);
                  setUserClubs(clubs);
                  saveClubMemberships(clubs);
                  setScreen(SCREENS.SETUP_PROFILE);
                } else {
                  _log('→ ONBOARDING (existing user but no vehicles — clearing hydrated data for clean re-onboarding)');
                  clearAll();
                  setScreen(SCREENS.ONBOARDING);
                }
              }
            }
          }
        } else {
          // New user — show onboarding
          if (adminIntent && user?.email === ADMIN_EMAIL) {
            _log('→ ADMIN (new user, admin intent)');
            setScreen(SCREENS.ADMIN);
          } else if (accountIntent) {
            _log('→ ACCOUNT (new user, account intent)');
            setScreen(SCREENS.ACCOUNT);
          } else if (joinClubSlug) {
            _log('→ JOIN_CLUB (new user, join intent)');
            setScreen(SCREENS.JOIN_CLUB);
          } else if (eventIntent) {
            _log('→ EVENT_DETAIL (new user, event intent — skip onboarding)');
            setIsPartialUser(true);
            setNeedsName(true);
            setSelectedEventId(eventIntent);
            setScreen(SCREENS.EVENT_DETAIL);
            // Auto-join the event's club (non-blocking)
            getPublicEventDetail(eventIntent).then(ev => {
              if (ev?.club_id && userId) {
                joinClub(ev.club_id, userId, 'event_link').then(() => loadUserClubs());
              }
            });
          } else {
            _log('→ ONBOARDING (new user, no Supabase profile)');
            setScreen(SCREENS.ONBOARDING);
          }
        }
      } catch (err) {
        _err('User check failed:', err);
        // Fallback: if local data exists, show dashboard instead of stuck on onboarding
        const fallbackVehicles = getVehicles();
        if (fallbackVehicles.length > 0) {
          reloadAll();
          setScreen(getInitialScreen());
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Load pending invitations
  useEffect(() => {
    if (!userId || !userEmail || isDemoMode) { setPendingInvitationCount(0); setPendingInvitations([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const count = await fetchInviteCount(userId, userEmail);
        if (cancelled) return;
        setPendingInvitationCount(count);
        if (count > 0) {
          const invitations = await fetchInvites(userId, userEmail);
          if (!cancelled) setPendingInvitations(invitations);
        } else {
          setPendingInvitations([]);
        }
      } catch (err) {
        console.error('[PM-Auth] Invitation load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Refresh today's check-in events whenever Dashboard becomes active
  // (e.g. after checking in on EventDetail and navigating back)
  useEffect(() => {
    if (screen === SCREENS.DASHBOARD && userId && !isDemoMode) {
      loadTodayCheckinEvents();
    }
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── On tab focus: validate collaborator access + process queued writes ───
  useEffect(() => {
    if (!userId || !userEmail || isDemoMode) return;

    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;

      // Refresh club memberships on resume — user may have joined a club
      // via an external link (route viewer, club invite, etc.)
      loadUserClubs();

      (async () => {
        // 0. In native app, sync subscription on resume (user may have upgraded in Safari)
        if (isNativeApp) {
          try {
            const profile = await checkUserExists(userId);
            if (profile) {
              const oldPlan = getSettings().subscription.plan;
              syncSubscriptionFromSupabase(profile);
              const newPlan = getSettings().subscription.plan;
              if (newPlan !== oldPlan && newPlan !== 'free') {
                reloadAll();
                triggerToast(`Plan updated to ${newPlan.charAt(0).toUpperCase() + newPlan.slice(1)}!`);
              }
            }
          } catch { /* Network error, ignore */ }
        }

        // 1. Process any queued writes that failed previously
        const pending = getPendingWriteCount();
        if (pending > 0) {
          console.log('[PM-Queue] Tab focused — processing', pending, 'pending writes...');
          const queueHandlers = {
            analysis: (p) => saveAnalysisToSupabase(p.userId, p.vehicleId, p.analysis),
            valueSnapshot: (p) => saveValueSnapshotToSupabase(p.userId, p.vehicleId, p.snapshot),
            maintenance: (p) => updateMaintenanceInSupabase(p.userId, p.vehicleId, p.maintenanceItems, p.healthScore, p.healthSummary),
            valuation: (p) => updateValuationInSupabase(p.userId, p.vehicleId, p.marketValue),
            serviceLog: (p) => saveServiceLog(p.userId, p.vehicleId, p.entry),
            mileageLog: (p) => saveMileageLog(p.userId, p.vehicleId, p.entry),
            vehicleMileage: (p) => updateVehicleMileage(p.userId, p.vehicleId, p.mileage),
            garageUpdate: (p) => updateGarageInSupabase(p.garageId, p.fields),
            profileUpdate: (p) => updateUserProfile(p.userId, p.fields),
            vehicle: (p) => createVehicle(p.vehicleData, p.userId, p.garageId),
            vehicleUpdate: (p) => updateVehicleInSupabase(p.vehicleId, p.fields),
            vehicleDelete: (p) => deleteVehicleFromSupabase(p.vehicleId),
            reminder: (p) => saveReminderToSupabase(p.userId, p.vehicleId, p.reminder),
            reminderDelete: (p) => deleteReminderFromSupabase(p.reminderId),
            backup: (p) => syncBackupDataType(p.userId, p.dataType, p.data),
          };
          await processQueue(queueHandlers);
        }

        // 2. Validate collaborator access (detect revocations)
        const currentGarages = getGarages();
        const shared = currentGarages.filter((g) => g.role === 'collaborator');
        if (shared.length === 0) return;

        let removed = false;
        for (const g of shared) {
          try {
            const perm = await getUserPermission(g.id, userId, userEmail);
            if (perm === null) {
              console.log('[PM-Sync] Access revoked for garage:', g.id);
              storageRemoveGarage(g.id);
              removed = true;
            }
          } catch {
            // Network error — ignore, will retry next focus
          }
        }
        if (removed) {
          reloadAll();
          setToastMessage('A shared garage was removed — access revoked by owner');
          setShowToast(true);
        }
      })();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, userEmail]);

  // ─── Stripe checkout return handling ───
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    const checkoutTier = params.get('tier');

    if (!checkoutStatus) return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);

    if (checkoutStatus === 'cancel') {
      triggerToast('Checkout cancelled');
      return;
    }

    if (checkoutStatus === 'success' && user) {
      triggerToast('Payment successful! Updating your plan…');

      // Poll Supabase for the webhook to update subscription_tier
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        attempts++;
        try {
          const profile = await checkUserExists(user.id);
          if (profile && profile.subscription_tier === checkoutTier) {
            syncSubscriptionFromSupabase(profile);
            reloadAll();
            triggerToast(`Upgraded to ${checkoutTier.charAt(0).toUpperCase() + checkoutTier.slice(1)}!`);
            return;
          }
        } catch (err) {
          console.error('[Checkout] Poll error:', err);
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          // Optimistic fallback — webhook may be slow
          console.log('[Checkout] Poll timed out — applying optimistic update');
          const TIER_CREDITS = { enthusiast: 100, collector: 250, showroom: 500, team: 500 };
          if (checkoutTier && TIER_CREDITS[checkoutTier]) {
            updateSettings({
              subscription: {
                plan: checkoutTier,
                creditsUsed: 0,
                creditsTotal: TIER_CREDITS[checkoutTier],
              },
            });
            reloadAll();
          }
          triggerToast(`Upgraded to ${checkoutTier ? checkoutTier.charAt(0).toUpperCase() + checkoutTier.slice(1) : 'paid plan'}!`);
        }
      };

      poll();
    }
  }, [user]);

  // ─── Stripe Connect & event payment return handling ───
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectStatus = params.get('connect');
    const connectClubId = params.get('club_id');
    const eventPayment = params.get('event_payment');
    const eventPaymentId = params.get('event_id');

    if (!connectStatus && !eventPayment) return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);

    // Connect onboarding return
    if (connectStatus === 'success' && connectClubId) {
      triggerToast('Checking Stripe Connect status…');
      checkConnectStatus(connectClubId)
        .then((status) => {
          if (status.onboarded) {
            triggerToast('Stripe Connect setup complete! You can now accept payments.');
          } else {
            triggerToast('Stripe Connect setup is pending — you may need to finish providing details.');
          }
        })
        .catch(() => {
          triggerToast('Could not verify Connect status. Please check the Payouts tab.');
        });
      return;
    }

    if (connectStatus === 'refresh' && connectClubId) {
      triggerToast('Stripe Connect session expired. Please try again from the Payouts tab.');
      return;
    }

    // Event payment return
    if (eventPayment === 'success' && eventPaymentId) {
      triggerToast('Payment successful! You are registered for this event.');
      setSelectedEventId(eventPaymentId);
      setScreen(SCREENS.EVENT_DETAIL);
      return;
    }

    if (eventPayment === 'cancel') {
      triggerToast('Event payment cancelled.');
      return;
    }
  }, []);

  // ─── Auth gate (after all hooks) ───
  if (authLoading) {
    return (
      <div style={{ minHeight: '100dvh', width: '100%', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!user) {
    // If user arrived via a public event link and hasn't clicked "Register" yet, show preview
    if (eventIntent && !eventLoginEmail) {
      return (
        <PublicEventPreview
          eventId={eventIntent}
          onLogin={(email) => setEventLoginEmail(email)}
        />
      );
    }
    return <Login initialEmail={initialEmail || eventLoginEmail || ''} joinClubSlug={joinClubSlug} />;
  }

  // ─── Profile loading gate — prevents flash of onboarding ───
  if (profileLoading) {
    return (
      <div style={{
        minHeight: '100dvh',
        width: '100%',
        background: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
      }}>
        {/* Animated car silhouette loader */}
        <div style={{ position: 'relative', width: 64, height: 64 }}>
          <div style={{
            width: 64, height: 64,
            borderRadius: '50%',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)',
            animation: 'pm-spin 0.9s linear infinite',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
              <path d="M5 17h2m10 0h2M5 17a2 2 0 01-2-2V9a2 2 0 012-2h1l2-3h8l2 3h1a2 2 0 012 2v6a2 2 0 01-2 2" />
              <circle cx="7.5" cy="17.5" r="1.5" />
              <circle cx="16.5" cy="17.5" r="1.5" />
            </svg>
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <p style={{
            color: 'var(--text-primary)',
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: 6,
          }}>
            {loadingMessage}
          </p>
          <p style={{
            color: 'var(--text-muted)',
            fontSize: '0.8125rem',
          }}>
            Proper Miles Mobile Garage
          </p>
        </div>

        <style>{`@keyframes pm-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* ─── Toast ─── */

  function triggerToast(message) {
    setToastMessage(message);
    setShowToast(true);
  }

  /* ─── Onboarding & Processing ─── */

  function handleOnboardingComplete(vehicleData) {
    // vehicleData already has id + garageId assigned by Onboarding
    setSelectedVehicleId(vehicleData.id);
    // Capture receipt photos in React state — they were stripped from localStorage to avoid bloat
    setPendingReceiptPhotos(vehicleData.receiptPhotos || []);
    setIsPartialUser(false);
    reloadAll();
    setScreen(SCREENS.PROCESSING);
  }

  function handleAnalysisComplete() {
    setPendingReceiptPhotos([]);
    reloadAll();
    setScreen(SCREENS.DETAIL);
    triggerToast('Vehicle analysis complete!');

    // Send "analysis complete" push notification (non-blocking, native app only)
    // Useful if user backgrounded the app while analysis was running
    if (isNativeApp && user?.id && !isDemoMode) {
      const vLabel = selectedVehicle
        ? [selectedVehicle.year, selectedVehicle.make, selectedVehicle.model].filter(Boolean).join(' ')
        : '';
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (supabaseUrl && anonKey) {
        fetch(`${supabaseUrl}/functions/v1/send-push-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${anonKey}`,
          },
          body: JSON.stringify({
            type: 'analysis_complete',
            user_id: user.id,
            vehicle_label: vLabel,
          }),
        }).catch(() => { /* non-blocking */ });
      }
    }
  }

  function handleReanalyze() {
    setScreen(SCREENS.PROCESSING);
  }

  /* ─── Navigation ─── */

  function handleOpenGarageVehicles(garageId) {
    setSelectedGarageId(garageId);
    setScreen(SCREENS.VEHICLE_LIST);
  }

  function handleOpenVehicleDetail(vehicleId) {
    setSelectedVehicleId(vehicleId);
    setScreen(SCREENS.DETAIL);
  }

  function handleOpenChat() {
    setScreen(SCREENS.CHAT);
  }

  function handleOpenChatWithContext(message) {
    setChatInitialMessage(message);
    setScreen(SCREENS.CHAT);
  }

  function handleCloseChat() {
    setChatInitialMessage(null);
    setScreen(SCREENS.DETAIL);
  }

  function handleOpenAutoAdvisor(mode) {
    setAutoAdvisorInitMode(mode || null);
    setScreen(SCREENS.AUTO_ADVISOR);
  }

  function handleCloseAutoAdvisor() {
    if (vehicles.length > 0) {
      setScreen(SCREENS.DASHBOARD);
    } else {
      setScreen(SCREENS.ONBOARDING);
    }
  }

  /* ─── Write Queue Processing ─── */

  /**
   * Process any queued Supabase writes that failed previously.
   * Called on app load, pull-to-refresh, and tab focus.
   */
  async function processWriteQueue() {
    const pending = getPendingWriteCount();
    if (pending === 0) return;
    console.log('[PM-Queue] Processing', pending, 'pending writes...');

    const handlers = {
      analysis: (p) => saveAnalysisToSupabase(p.userId, p.vehicleId, p.analysis),
      valueSnapshot: (p) => saveValueSnapshotToSupabase(p.userId, p.vehicleId, p.snapshot),
      maintenance: (p) => updateMaintenanceInSupabase(p.userId, p.vehicleId, p.maintenanceItems, p.healthScore, p.healthSummary),
      valuation: (p) => updateValuationInSupabase(p.userId, p.vehicleId, p.marketValue),
      serviceLog: (p) => saveServiceLog(p.userId, p.vehicleId, p.entry),
      mileageLog: (p) => saveMileageLog(p.userId, p.vehicleId, p.entry),
      vehicleMileage: (p) => updateVehicleMileage(p.userId, p.vehicleId, p.mileage),
      garageUpdate: (p) => updateGarageInSupabase(p.garageId, p.fields),
      profileUpdate: (p) => updateUserProfile(p.userId, p.fields),
      vehicle: (p) => createVehicle(p.vehicleData, p.userId, p.garageId),
      vehicleUpdate: (p) => updateVehicleInSupabase(p.vehicleId, p.fields),
      vehicleDelete: (p) => deleteVehicleFromSupabase(p.vehicleId),
      reminder: (p) => saveReminderToSupabase(p.userId, p.vehicleId, p.reminder),
      reminderDelete: (p) => deleteReminderFromSupabase(p.reminderId),
      backup: (p) => syncBackupDataType(p.userId, p.dataType, p.data),
    };

    const result = await processQueue(handlers);
    if (result.succeeded > 0) {
      console.log('[PM-Queue] Successfully retried', result.succeeded, 'writes');
    }
    if (result.dropped > 0) {
      console.warn('[PM-Queue] Dropped', result.dropped, 'writes (max retries exceeded)');
    }
  }

  /* ─── Sync reminder statuses to Supabase ─── */

  /**
   * Compare localStorage reminders against Supabase. For any reminder that's
   * 'completed' locally but still 'pending' in Supabase, push the update.
   * This catches completions where the Supabase write failed silently.
   */
  async function syncReminderStatuses(uid) {
    if (!uid) return;
    try {
      const localReminders = getReminders().filter((r) => r.status === 'completed');
      if (localReminders.length === 0) return;

      const sbReminders = await loadUserReminders(uid);
      if (!sbReminders || sbReminders.length === 0) return;

      const sbByIdMap = new Map(sbReminders.map((r) => [r.id, r]));
      let synced = 0;

      for (const local of localReminders) {
        const sb = sbByIdMap.get(local.id);
        if (sb && sb.status === 'pending') {
          try {
            await saveReminderToSupabase(uid, local.vehicleId, local);
            synced++;
          } catch (err) {
            if (import.meta.env.DEV) console.error('[PM-Sync] Failed to sync reminder:', local.id, err.message);
          }
        }
      }

      if (synced > 0) {
        console.log('[PM-Sync] Synced', synced, 'completed reminder(s) to Supabase');
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[PM-Sync] syncReminderStatuses error:', err.message);
    }
  }

  /* ─── Push unsynced vehicles to Supabase ─── */

  /**
   * Compare localStorage vehicles against Supabase, push any that are missing.
   * This catches vehicles created when the Supabase write failed silently.
   */
  async function pushUnsyncedVehicles(uid) {
    if (!uid) return;
    try {
      const localVehicles = getVehicles();
      if (localVehicles.length === 0) return;

      const sbVehicles = await loadUserVehicles(uid);
      const sbIds = new Set(sbVehicles.map((v) => v.id));

      let pushed = 0;
      let rekeyed = 0;
      const rekeyMap = {}; // oldId → newId

      // Fuzzy vehicle match: same year+make, and one model is a prefix of the other
      function fuzzyMatchVehicle(localV, sbList) {
        const lYear = String(localV.year || '');
        const lMake = (localV.make || '').toLowerCase().trim();
        const lModel = (localV.model || '').toLowerCase().trim();
        for (const sv of sbList) {
          if (String(sv.year || '') === lYear &&
              (sv.make || '').toLowerCase().trim() === lMake) {
            const sModel = (sv.model || '').toLowerCase().trim();
            if (lModel === sModel || lModel.startsWith(sModel) || sModel.startsWith(lModel)) {
              return sv;
            }
          }
        }
        return null;
      }

      for (const localV of localVehicles) {
        // Skip if already in Supabase by ID
        if (sbIds.has(localV.id)) continue;

        // Check fuzzy match (year+make, model prefix)
        const sbMatch = fuzzyMatchVehicle(localV, sbVehicles);

        if (sbMatch) {
          // Vehicle exists in Supabase with a different ID — re-key local to match
          if (sbMatch.id !== localV.id) {
            const oldId = localV.id;
            rekeyMap[oldId] = sbMatch.id;
            console.log('[PM-Sync] Re-keying vehicle by fingerprint:', oldId, '→', sbMatch.id, `(${fp})`);
            rekeyed++;
          }
          continue;
        }

        // This vehicle only exists locally — push it to Supabase
        console.log('[PM-Sync] Pushing unsynced vehicle:', localV.year, localV.make, localV.model);
        try {
          const created = await pushVehicleToSupabase(localV, uid);
          if (created) {
            if (created.id !== localV.id) {
              rekeyMap[localV.id] = created.id;
            }
            pushed++;
          }
        } catch (err) {
          console.error('[PM-Sync] Failed to push vehicle:', localV.make, localV.model, '—', err.message);
        }
      }

      // Apply all re-keys in a single pass
      const rekeyEntries = Object.entries(rekeyMap);
      if (rekeyEntries.length > 0) {
        const allV = getVehicles();
        const allAnalyses = getAllAnalyses();
        const allGarages = getGarages();

        for (const [oldId, newId] of rekeyEntries) {
          // Re-key vehicle
          const match = allV.find((v) => v.id === oldId);
          if (match) match.id = newId;

          // Re-key garage vehicleIds
          for (const g of allGarages) {
            if (g.vehicleIds) {
              const idx = g.vehicleIds.indexOf(oldId);
              if (idx !== -1) g.vehicleIds[idx] = newId;
            }
          }

          // Re-key analysis data
          if (allAnalyses[oldId]) {
            allAnalyses[newId] = allAnalyses[oldId];
            delete allAnalyses[oldId];
          }

          // Update selectedVehicleId if it was re-keyed
          if (selectedVehicleId === oldId) {
            setSelectedVehicleId(newId);
          }
        }

        saveVehicles(allV);
        saveGarages(allGarages);
        localStorage.setItem('pm_analyses', JSON.stringify(allAnalyses));
        console.log('[PM-Sync] Re-keyed', rekeyEntries.length, 'vehicle IDs');
      }

      if (pushed > 0 || rekeyed > 0) {
        console.log('[PM-Sync] Pushed', pushed, 'vehicles, re-keyed', rekeyed);
        reloadAll();
      }
    } catch (err) {
      console.error('[PM-Sync] pushUnsyncedVehicles error:', err.message);
    }
  }

  /* ─── Push local-only analyses to Supabase ─── */

  async function pushUnsyncedAnalyses(uid) {
    if (!uid) return;
    try {
      const localAnalyses = getAllAnalyses();
      const vehicleIds = Object.keys(localAnalyses);
      if (vehicleIds.length === 0) return;

      let pushed = 0;
      for (const vid of vehicleIds) {
        // Skip localStorage-style IDs (not yet in Supabase)
        if (vid.startsWith('vehicle_')) continue;
        const localData = localAnalyses[vid];
        if (!localData || !localData.analyzedAt) continue;

        try {
          // Check if Supabase already has this analysis
          const remote = await loadVehicleAnalysis(vid);
          if (!remote) {
            await saveAnalysisToSupabase(uid, vid, localData);
            pushed++;
            console.log('[PM-Sync] Pushed local analysis to Supabase for vehicle:', vid);
          }
        } catch (err) {
          console.error('[PM-Sync] Failed to push analysis for vehicle:', vid, '-', err.message);
        }
      }
      if (pushed > 0) {
        console.log('[PM-Sync] Pushed', pushed, 'local analyses to Supabase');
      }
    } catch (err) {
      console.error('[PM-Sync] pushUnsyncedAnalyses error:', err.message);
    }
  }

  /* ─── Push local vehicle photos to Supabase ─── */

  async function pushUnsyncedPhotos(uid) {
    if (!uid) return;
    try {
      const localVehicles = getVehicles();
      let pushed = 0;
      for (const v of localVehicles) {
        if (!v.photo || !v.id || String(v.id).startsWith('vehicle_')) continue;
        try {
          const hasPhoto = await vehicleHasPhotoInSupabase(v.id);
          if (!hasPhoto) {
            await updateVehicleInSupabase(v.id, { photo: v.photo });
            pushed++;
            console.log('[PM-Sync] Pushed photo to Supabase for vehicle:', v.id);
          }
        } catch (err) {
          console.error('[PM-Sync] Failed to push photo for vehicle:', v.id, '-', err.message);
        }
      }
      if (pushed > 0) {
        console.log('[PM-Sync] Pushed', pushed, 'vehicle photos to Supabase');
      }
    } catch (err) {
      console.error('[PM-Sync] pushUnsyncedPhotos error:', err.message);
    }
  }

  /* ─── Push local garage photos to Supabase ─── */

  async function pushUnsyncedGaragePhotos(uid) {
    if (!uid) return;
    try {
      const localGarages = getGarages();
      let pushed = 0;
      for (const g of localGarages) {
        const gPhoto = g.bannerPhoto || g.photo;
        if (!gPhoto || !g.id || String(g.id).startsWith('garage_')) continue;
        if (g.role === 'collaborator') continue;
        try {
          const hasPhoto = await garageHasPhotoInSupabase(g.id);
          if (!hasPhoto) {
            await updateGarageInSupabase(g.id, { bannerPhoto: gPhoto });
            pushed++;
            console.log('[PM-Sync] Pushed photo to Supabase for garage:', g.id);
          }
        } catch (err) {
          console.error('[PM-Sync] Failed to push photo for garage:', g.id, '-', err.message);
        }
      }
      if (pushed > 0) {
        console.log('[PM-Sync] Pushed', pushed, 'garage photos to Supabase');
      }
    } catch (err) {
      console.error('[PM-Sync] pushUnsyncedGaragePhotos error:', err.message);
    }
  }

  /* ─── Sync owned garages to Supabase (name, vehicle garage_id) ─── */

  async function syncOwnedGaragesToSupabase() {
    if (!user) return;
    const currentGarages = getGarages();
    const currentVehicles = getVehicles();

    for (const g of currentGarages) {
      // Skip collaborator garages — we don't own them
      if (g.role === 'collaborator') continue;
      // Skip garages with localStorage IDs — not yet in Supabase
      if (String(g.id).startsWith('garage_')) continue;

      try {
        // Sync garage name/location to Supabase so collaborators see correct name
        await updateGarageInSupabase(g.id, { name: g.name, location: g.location || '' });

        // Backfill garage_id on vehicles that have NULL in Supabase
        const garageVehicleIds = currentVehicles
          .filter((v) => v.garageId === g.id)
          .map((v) => v.id);
        if (garageVehicleIds.length > 0) {
          await backfillVehicleGarageIds(g.id, garageVehicleIds);
        }
      } catch {
        // Best-effort sync — don't break the flow
      }
    }
  }

  /* ─── Pull-to-Refresh ─── */

  async function handlePullRefresh() {
    try {
      // 1. Validate collaborator garages (remove revoked)
      const currentGarages = getGarages();
      let removed = false;
      for (const g of currentGarages) {
        if (g.role === 'collaborator' && g.id) {
          try {
            const perm = await getUserPermission(g.id, user.id, user.email);
            if (perm === null) {
              storageRemoveGarage(g.id);
              removed = true;
            }
          } catch {
            // Network error — skip
          }
        }
      }

      // 2. Sync subscription/credits from Supabase
      try {
        const profile = await checkUserExists(user.id);
        if (profile) syncSubscriptionFromSupabase(profile);
      } catch {
        // Swallow
      }

      // 3. Process any queued Supabase writes that failed previously
      await processWriteQueue();

      // 3b. Sync reminder statuses — catch completions that failed to reach Supabase
      await syncReminderStatuses(user.id);

      // 4. Push any vehicles that exist locally but not in Supabase
      await pushUnsyncedVehicles(user.id);

      // 4b. Push any analyses that exist locally but not in Supabase
      await pushUnsyncedAnalyses(user.id);

      // 4c. Push any vehicle photos that exist locally but not in Supabase
      await pushUnsyncedPhotos(user.id);

      // 4d. Push any garage photos that exist locally but not in Supabase
      await pushUnsyncedGaragePhotos(user.id);

      // 5. Sync owned garage data to Supabase (name, vehicle garage_id)
      await syncOwnedGaragesToSupabase();

      // 6. Refresh shared garage data (vehicles, analyses, service logs, value history)
      try {
        const refreshedGarages = getGarages();
        const sharedGarages = refreshedGarages.filter((g) => g.role === 'collaborator');

        for (const sg of sharedGarages) {
          // Refresh vehicles for each shared garage
          const sgVehicles = await loadGarageVehicles(sg.id);
          const vehicleIds = [];

          for (const v of sgVehicles) {
            storageSaveVehicle({
              id: v.id,
              garageId: v.garage_id,
              year: v.year || '',
              make: v.make || '',
              model: v.model || '',
              trim: v.trim || '',
              mileage: v.current_mileage || '',
              tmu: v.is_tmu || false,
              condition: v.condition || '',
              primaryUse: v.primary_use || [],
              photo: v.banner_photo || null,
              lastMileageUpdateAt: v.last_mileage_update_at || null,
            });
            vehicleIds.push(v.id);
          }

          // Update garage vehicleIds (owner may have added/removed vehicles)
          upsertGarage({ ...sg, vehicleIds });

          // Refresh analyses, service logs, value history for ALL shared vehicles
          for (const vid of vehicleIds) {
            try {
              const analysis = await loadVehicleAnalysis(vid);
              if (analysis) {
                storageSaveAnalysis(vid, analysis);
              }
            } catch { /* best-effort */ }

            try {
              const logs = await loadVehicleServiceLogs(vid);
              if (logs.length > 0) {
                const mapped = logs.map(mapServiceLogToLocal);
                const existing = getServiceHistory();
                const existingIds = new Set(existing.map((e) => e.id));
                const newEntries = mapped.filter((e) => !existingIds.has(e.id));
                if (newEntries.length > 0) {
                  saveServiceHistory([...newEntries, ...existing]);
                }
              }
            } catch { /* best-effort */ }

            try {
              const valueHistory = await loadVehicleValueHistory(vid);
              if (valueHistory.length > 0) {
                const raw = localStorage.getItem('pm_value_history');
                const allValues = raw ? JSON.parse(raw) : {};
                allValues[vid] = valueHistory;
                localStorage.setItem('pm_value_history', JSON.stringify(allValues));
              }
            } catch { /* best-effort */ }

            // Refresh reminders for shared vehicles
            try {
              const vehicleReminders = await loadVehicleReminders(vid);
              if (vehicleReminders.length > 0) {
                const allReminders = getReminders();
                const others = allReminders.filter((r) => r.vehicleId !== vid);
                const merged = [...others, ...vehicleReminders.map(mapReminderToLocal)];
                saveReminders(merged);
              }
            } catch { /* best-effort */ }
          }
        }
        if (sharedGarages.length > 0) {
          console.log('[PM-Refresh] Refreshed', sharedGarages.length, 'shared garages');
        }
      } catch (err) {
        console.error('[PM-Refresh] Shared garage refresh failed:', err.message);
      }

      // 7. Refresh all reminders from Supabase (not just shared)
      if (user?.id) {
        try {
          const sbReminders = await loadUserReminders(user.id);
          if (sbReminders.length > 0) {
            const merged = sbReminders.map(mapReminderToLocal);
            saveReminders(merged);
          }
        } catch { /* best-effort */ }
      }

      // 8. Refresh pending invitations
      await refreshInvitations();

      // 9. Reload state from localStorage
      reloadAll();

      if (removed) {
        triggerToast('A shared garage was removed — access revoked by owner');
      }
    } catch (err) {
      console.error('[PM-Refresh] Pull-to-refresh error:', err);
    }
  }

  /* ─── Data Operations ─── */

  function handleAddServiceEntry(entry) {
    const updated = storageAddServiceEntry(selectedVehicleId, entry);
    setServiceHistory(updated);
    setVehicles(getVehicles());

    // Reliable Supabase save (queues for retry on failure)
    if (user) {
      reliableWrite('serviceLog', { userId: user.id, vehicleId: selectedVehicleId, entry }, () =>
        saveServiceLog(user.id, selectedVehicleId, entry)
      );
    }
  }

  function handleUpdateServiceEntry(id, fields) {
    const updated = storageUpdateServiceEntry(id, fields);
    setServiceHistory(updated);
  }

  function handleRemoveServiceEntry(id) {
    const updated = storageRemoveServiceEntry(id);
    setServiceHistory(updated);
  }

  /* ─── Reminder handlers ─── */

  function handleAddReminder(reminder) {
    const entry = storageAddReminder({ ...reminder, vehicleId: selectedVehicleId });
    setReminders(getReminders());
    if (user) {
      reliableWrite('reminder', { userId: user.id, vehicleId: selectedVehicleId, reminder: entry }, () =>
        saveReminderToSupabase(user.id, selectedVehicleId, entry)
      );
    }
    return entry;
  }

  function handleUpdateReminder(id, fields) {
    storageUpdateReminder(id, fields);
    setReminders(getReminders());
    if (user) {
      const updated = getReminders().find((r) => r.id === id);
      if (updated) {
        reliableWrite('reminder', { userId: user.id, vehicleId: updated.vehicleId, reminder: updated }, () =>
          saveReminderToSupabase(user.id, updated.vehicleId, updated)
        );
      }
    }
  }

  function handleCompleteReminder(id) {
    storageCompleteReminder(id);
    setReminders(getReminders());
    if (user) {
      const updated = getReminders().find((r) => r.id === id);
      if (updated) {
        reliableWrite('reminder', { userId: user.id, vehicleId: updated.vehicleId, reminder: updated }, () =>
          saveReminderToSupabase(user.id, updated.vehicleId, updated)
        );
      }
    }
  }

  function handleDeleteReminder(id) {
    storageRemoveReminder(id);
    setReminders(getReminders());
    if (user) {
      reliableWrite('reminderDelete', { reminderId: id }, () =>
        deleteReminderFromSupabase(id)
      );
    }
  }

  function handleAddGarage(name, photo) {
    // Guard: enforce garage limit — only count OWNED garages (exclude shared/collaborator)
    const limits = getSubscriptionLimits();
    const ownedGarageCount = garages.filter(g => g.role !== 'collaborator').length;
    if (ownedGarageCount >= limits.maxGarages) {
      setShowUpgradeModal(true);
      return;
    }
    storageAddGarage({ name, photo });
    setGarages(getGarages());
  }

  function handleUpdateGarage(id, fields) {
    storageUpdateGarage(id, fields);
    setGarages(getGarages());
    // Reliable sync to Supabase so collaborators see updated name/photo/location
    reliableWrite('garageUpdate', { garageId: id, fields }, () =>
      updateGarageInSupabase(id, fields)
    );
  }

  function handleDeleteGarage(id) {
    storageRemoveGarage(id);
    reloadAll();
    // If we were viewing that garage, go back to list
    if (selectedGarageId === id) {
      setSelectedGarageId(null);
    }
  }

  function handleUpdateVehicle(vehicleData) {
    storageSaveVehicle(vehicleData);
    setVehicles(getVehicles());

    // Sync all editable vehicle fields to Supabase
    if (user && vehicleData.id && !String(vehicleData.id).startsWith('vehicle_')) {
      const syncFields = {};
      if (vehicleData.photo !== undefined) syncFields.photo = vehicleData.photo;
      if (vehicleData.condition !== undefined) syncFields.condition = vehicleData.condition;
      if (vehicleData.trim !== undefined) syncFields.trim = vehicleData.trim;
      if (vehicleData.primaryUse !== undefined) syncFields.primaryUse = vehicleData.primaryUse;
      if (vehicleData.year !== undefined) syncFields.year = vehicleData.year;
      if (vehicleData.make !== undefined) syncFields.make = vehicleData.make;
      if (vehicleData.model !== undefined) syncFields.model = vehicleData.model;
      if (vehicleData.tmu !== undefined) syncFields.tmu = vehicleData.tmu;
      if (vehicleData.mileage !== undefined) syncFields.mileage = vehicleData.mileage;
      if (vehicleData.internalNotes !== undefined) syncFields.internalNotes = vehicleData.internalNotes;
      if (Object.keys(syncFields).length > 0) {
        reliableWrite('vehicleUpdate', { vehicleId: vehicleData.id, fields: syncFields }, () =>
          updateVehicleInSupabase(vehicleData.id, syncFields)
        );
      }
    }
  }

  async function handleRemoveVehicle(vehicleId) {
    storageRemoveVehicle(vehicleId);
    reloadAll();
    // Delete from Supabase so it doesn't reappear on next sign-in.
    // Await the delete so it completes before the user can sign out.
    // Falls back to write queue if it fails (retried on next app load).
    await reliableWrite('vehicleDelete', { vehicleId }, () =>
      deleteVehicleFromSupabase(vehicleId)
    );
  }

  function handleMoveVehicle(vehicleId, toGarageId) {
    let targetId = toGarageId;
    // Handle special garage types — create if they don't exist
    if (toGarageId === '__sold__' || toGarageId === '__archived__') {
      const garageLabel = toGarageId === '__sold__' ? 'Sold Vehicles' : 'Archived';
      const currentGarages = getGarages();
      let specialGarage = currentGarages.find((g) => g.name === garageLabel);
      if (!specialGarage) {
        specialGarage = storageAddGarage({ name: garageLabel });
      }
      targetId = specialGarage.id;
    }
    storageMoveVehicle(vehicleId, targetId);
    reloadAll();
  }

  function handleReset() {
    clearAll();
    setBackupUserId(null);
    setGarages([]);
    setVehicles([]);
    setAnalyses({});
    setServiceHistory([]);
    setSelectedGarageId(null);
    setSelectedVehicleId(null);
    setScreen(SCREENS.ONBOARDING);
  }

  /* ─── Invitations ─── */

  async function refreshInvitations() {
    if (!user) return;
    try {
      const count = await fetchInviteCount(user.id, user.email);
      setPendingInvitationCount(count);
      if (count > 0) {
        const invitations = await fetchInvites(user.id, user.email);
        setPendingInvitations(invitations);
      } else {
        setPendingInvitations([]);
      }
    } catch (err) {
      console.error('[PM-Auth] Invitation refresh failed:', err);
    }
  }

  async function handleAcceptInvitation(invitationId) {
    const invitation = pendingInvitations.find((inv) => inv.id === invitationId);
    const result = await acceptInvite(invitationId, user.id, user.email);
    console.log('[PM-Invite] Accept result:', { error: result.error, hasData: !!result.data, garage: result.data?.garage });
    if (result.error) {
      triggerToast(result.error);
      return;
    }
    // Add shared garage to localStorage
    const garageData = result.data?.garage || invitation?.garages;
    console.log('[PM-Invite] Garage data:', garageData ? { id: garageData.id, name: garageData.name } : 'NULL', 'fallback from invitation:', invitation?.garages ? { id: invitation.garages.id, name: invitation.garages.name } : 'NULL');
    if (garageData) {
      // Load the shared garage's vehicles via RPC (bypasses RLS)
      let garageVehicleIds = [];
      try {
        const sbVehicles = await loadGarageVehicles(garageData.id);
        console.log('[PM-Invite] Loaded', sbVehicles.length, 'vehicles for garage', garageData.id, sbVehicles.map(v => ({ id: v.id, make: v.make, model: v.model, garage_id: v.garage_id })));
        if (sbVehicles.length > 0) {
          for (const v of sbVehicles) {
            storageSaveVehicle({
              id: v.id,
              garageId: v.garage_id,
              year: v.year || '',
              make: v.make || '',
              model: v.model || '',
              trim: v.trim || '',
              mileage: v.current_mileage || '',
              tmu: v.is_tmu || false,
              condition: v.condition || '',
              primaryUse: v.primary_use || [],
              photo: v.banner_photo || null,
              lastMileageUpdateAt: v.last_mileage_update_at || null,
            });
            garageVehicleIds.push(v.id);
          }
          console.log('[Invitation] Loaded', sbVehicles.length, 'vehicles for shared garage');

          // Load shared data for each vehicle: analysis, service logs, value history
          for (const v of sbVehicles) {
            try {
              // 1. Analysis (via existing get_vehicle_analysis RPC)
              const analysis = await loadVehicleAnalysis(v.id);
              if (analysis) {
                storageSaveAnalysis(v.id, analysis);
              }

              // 2. Service logs (via new get_vehicle_service_logs RPC)
              const logs = await loadVehicleServiceLogs(v.id);
              if (logs.length > 0) {
                const mapped = logs.map(mapServiceLogToLocal);
                const existingHistory = getServiceHistory();
                const existingIds = new Set(existingHistory.map((e) => e.id));
                const newEntries = mapped.filter((e) => !existingIds.has(e.id));
                if (newEntries.length > 0) {
                  saveServiceHistory([...newEntries, ...existingHistory]);
                }
              }

              // 3. Value history (via new get_vehicle_value_snapshots RPC)
              const valueHistory = await loadVehicleValueHistory(v.id);
              if (valueHistory.length > 0) {
                const raw = localStorage.getItem('pm_value_history');
                const allValues = raw ? JSON.parse(raw) : {};
                allValues[v.id] = valueHistory;
                localStorage.setItem('pm_value_history', JSON.stringify(allValues));
              }
            } catch (dataErr) {
              console.error('[Invitation] Failed to load shared data for vehicle:', v.id, dataErr.message);
            }
          }
        }
      } catch (err) {
        console.error('[Invitation] Failed to load garage vehicles:', err.message);
      }

      const garageEntry = {
        id: garageData.id,
        name: garageData.name,
        bannerPhoto: garageData.banner_photo || null,
        vehicleIds: garageVehicleIds,
        location: garageData.location || '',
        owner: garageData.owner_id || '',
        collaborators: [],
        role: 'collaborator',
      };
      console.log('[PM-Invite] Saving shared garage:', { id: garageEntry.id, name: garageEntry.name, vehicleCount: garageVehicleIds.length });
      upsertGarage(garageEntry);
      reloadAll();
    }
    triggerToast('Invitation accepted! Garage added.');
    await refreshInvitations();
  }

  async function handleDeclineInvitation(invitationId) {
    const result = await declineInvite(invitationId);
    if (result.error) {
      triggerToast(result.error);
      return;
    }
    triggerToast('Invitation declined');
    await refreshInvitations();
  }

  function handleAddVehicleToGarage(garageId) {
    const limits = getSubscriptionLimits();
    // Only count OWNED vehicles (exclude shared/collaborator garage vehicles)
    if (getOwnedVehicleCount() >= limits.maxVehicles) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedGarageId(garageId);
    setScreen(SCREENS.ONBOARDING);
  }

  /* ─── Bottom Nav ─── */

  function handleNavTab(tabId) {
    if (tabId === 'dashboard') setScreen(SCREENS.DASHBOARD);
    else if (tabId === 'garage') setScreen(SCREENS.GARAGE_LIST);
    else if (tabId === 'auto_advisor') setScreen(SCREENS.AUTO_ADVISOR);
    else if (tabId === 'events') setScreen(SCREENS.EVENTS);
    else if (tabId === 'routes') setScreen(SCREENS.ROUTES);
    else if (tabId === 'club_admin') setScreen(SCREENS.CLUB_ADMIN);
    else if (tabId === 'settings') setScreen(SCREENS.SETTINGS);
  }

  function getActiveTab() {
    if (screen === SCREENS.DASHBOARD) return 'dashboard';
    if (
      screen === SCREENS.GARAGE_LIST ||
      screen === SCREENS.VEHICLE_LIST ||
      screen === SCREENS.DETAIL
    )
      return 'garage';
    if (screen === SCREENS.AUTO_ADVISOR) return 'dashboard'; // Advisor removed from nav, highlight Dashboard
    if (screen === SCREENS.EVENTS || screen === SCREENS.EVENT_DETAIL) return 'events';
    if (screen === SCREENS.ROUTES || screen === SCREENS.ROUTE_LIST || screen === SCREENS.ROUTE_BUILDER) return 'routes';
    if (screen === SCREENS.CLUB_ADMIN) return 'club_admin';
    if (screen === SCREENS.SETTINGS) return 'settings';
    return 'dashboard';
  }

  const showNav = NAV_SCREENS.includes(screen) && (vehicles.length > 0 || isPartialUser);

  // Intercept: show CollectName for partial users (event/route signups) missing first_name
  if (needsName && screen !== SCREENS.ONBOARDING) {
    return (
      <CollectName
        onComplete={(firstName, lastName) => {
          setNeedsName(false);
          // Also update localStorage profile if it exists
          const profile = JSON.parse(localStorage.getItem('pm_user_profile') || '{}');
          profile.firstName = firstName;
          profile.lastName = lastName;
          localStorage.setItem('pm_user_profile', JSON.stringify(profile));
        }}
      />
    );
  }

  // Intercept: show CollectContact when user is missing email or phone
  // (only for non-onboarding screens — onboarding handles this in StepAccount)
  if (needsContact && screen !== SCREENS.ONBOARDING) {
    return (
      <CollectContact
        type={needsContact}
        onComplete={() => setNeedsContact(null)}
      />
    );
  }

  return (
    <>
      <main className={`app-content${showNav ? ' has-nav' : ''}`}>
      {screen === SCREENS.ONBOARDING && (
        <Onboarding
          garages={garages}
          targetGarageId={selectedGarageId}
          onComplete={handleOnboardingComplete}
          onCancel={vehicles.length > 0 ? () => setScreen(SCREENS.DASHBOARD) : null}
        />
      )}
      {screen === SCREENS.PROCESSING && (
        <Processing
          vehicle={selectedVehicle}
          vehicleId={selectedVehicleId}
          serviceLog={selectedServiceHistory}
          receiptPhotos={pendingReceiptPhotos}
          userId={user?.id}
          onComplete={handleAnalysisComplete}
          onAddServiceEntries={(vid, entries) => {
            entries.forEach((entry) => {
              storageAddServiceEntry(vid, entry);
              if (user) {
                reliableWrite('serviceLog', { userId: user.id, vehicleId: vid, entry }, () =>
                  saveServiceLog(user.id, vid, entry)
                );
              }
            });
            setServiceHistory(getServiceHistory());
            setVehicles(getVehicles());
          }}
          onToast={triggerToast}
        />
      )}
      {screen === SCREENS.SETUP_PROFILE && (
        <Suspense fallback={null}>
          <SetupProfileLanding onStartSetup={() => setScreen(SCREENS.ONBOARDING)} />
        </Suspense>
      )}
      {screen === SCREENS.DASHBOARD && isPartialUser && (
        <Suspense fallback={null}>
          <SetupProfileLanding onStartSetup={() => setScreen(SCREENS.ONBOARDING)} />
        </Suspense>
      )}
      {screen === SCREENS.DASHBOARD && !isPartialUser && (
        <PullToRefresh onRefresh={handlePullRefresh}>
          <Dashboard
            garages={garages}
            vehicles={vehicles}
            analyses={analyses}
            serviceHistory={serviceHistory}
            pendingInvitations={pendingInvitations}
            pendingInvitationCount={pendingInvitationCount}
            onAcceptInvitation={handleAcceptInvitation}
            onDeclineInvitation={handleDeclineInvitation}
            onGoToGarage={handleOpenGarageVehicles}
            onGoToVehicle={handleOpenVehicleDetail}
            onOpenAutoAdvisor={handleOpenAutoAdvisor}
            onAddVehicle={() => {
              if (garages.length > 0) handleAddVehicleToGarage(garages[0].id);
            }}
            onUpgrade={() => setShowUpgradeModal(true)}
            reminders={reminders}
            onGoToEvents={demoVariant === 'alex' ? undefined : () => setScreen(SCREENS.EVENTS)}
            todayCheckinEvents={demoVariant === 'alex' ? [] : todayCheckinEvents}
            onGoToEventDetail={(eventId) => {
              setSelectedEventId(eventId);
              setScreen(SCREENS.EVENT_DETAIL);
            }}
            onViewRoute={(slug) => {
              setRouteSlugForViewer(slug);
              setRouteViewerReturnScreen(SCREENS.DASHBOARD);
              setScreen(SCREENS.ROUTE_VIEWER);
            }}
            onGoToRoutes={demoVariant === 'alex' ? undefined : () => setScreen(SCREENS.ROUTES)}
            onResumeNavigation={(slug) => {
              setRouteSlugForViewer(slug);
              setRouteViewerReturnScreen(SCREENS.DASHBOARD);
              setScreen(SCREENS.ROUTE_VIEWER);
            }}
          />
        </PullToRefresh>
      )}
      {screen === SCREENS.GARAGE_LIST && isPartialUser && (
        <Suspense fallback={null}>
          <SetupProfileLanding onStartSetup={() => setScreen(SCREENS.ONBOARDING)} />
        </Suspense>
      )}
      {screen === SCREENS.GARAGE_LIST && !isPartialUser && (
        <GarageList
          garages={garages}
          vehicles={vehicles}
          currentUserEmail={user.email}
          onSelectGarage={handleOpenGarageVehicles}
          onAddGarage={handleAddGarage}
          onUpdateGarage={handleUpdateGarage}
          onDeleteGarage={handleDeleteGarage}
          onToast={triggerToast}
        />
      )}
      {screen === SCREENS.VEHICLE_LIST && (
        <PullToRefresh onRefresh={handlePullRefresh}>
          <VehicleList
            garage={selectedGarage}
            vehicles={vehicles.filter((v) => v.garageId === selectedGarageId)}
            analyses={analyses}
            garages={garages}
            onSelectVehicle={handleOpenVehicleDetail}
            onAddVehicle={() => handleAddVehicleToGarage(selectedGarageId)}
            onBack={() => setScreen(SCREENS.GARAGE_LIST)}
            onUpdateGarage={handleUpdateGarage}
            onToast={triggerToast}
            onReferral={() => setScreen(SCREENS.REFERRALS)}
            isDemoMode={isDemoMode}
          />
        </PullToRefresh>
      )}
      {screen === SCREENS.DETAIL && (
        <PullToRefresh onRefresh={handlePullRefresh}>
          <VehicleDetail
            vehicle={selectedVehicle}
            analysis={selectedAnalysis}
            serviceHistory={selectedServiceHistory}
            garage={selectedGarage}
            onOpenChat={handleOpenChat}
            onOpenChatWithContext={handleOpenChatWithContext}
            onBack={() => setScreen(SCREENS.VEHICLE_LIST)}
            onOpenAutoAdvisor={handleOpenAutoAdvisor}
            onAddServiceEntry={handleAddServiceEntry}
            onUpdateServiceEntry={handleUpdateServiceEntry}
            onRemoveServiceEntry={handleRemoveServiceEntry}
            onUpdateVehicle={handleUpdateVehicle}
            onRemoveVehicle={handleRemoveVehicle}
            onMoveVehicle={handleMoveVehicle}
            onReanalyze={handleReanalyze}
            onAnalysisUpdated={reloadAll}
            onToast={triggerToast}
            garages={garages}
            reminders={reminders.filter((r) => r.vehicleId === selectedVehicleId)}
            onAddReminder={handleAddReminder}
            onUpdateReminder={handleUpdateReminder}
            onCompleteReminder={handleCompleteReminder}
            onDeleteReminder={handleDeleteReminder}
          />
        </PullToRefresh>
      )}
      {screen === SCREENS.CHAT && (
        <Chat
          vehicle={selectedVehicle}
          vehicleId={selectedVehicleId}
          analysis={selectedAnalysis}
          serviceHistory={selectedServiceHistory}
          initialMessage={chatInitialMessage}
          onBack={handleCloseChat}
          onToast={triggerToast}
        />
      )}
      {screen === SCREENS.AUTO_ADVISOR && (
        <AutoAdvisor onBack={handleCloseAutoAdvisor} vehicles={vehicles} initMode={autoAdvisorInitMode} />
      )}
      {screen === SCREENS.SETTINGS && (
        <Settings onReset={handleReset} onToast={triggerToast} onAdmin={user?.email === ADMIN_EMAIL ? () => setScreen(SCREENS.ADMIN) : undefined} onSuperAdmin={user?.email === ADMIN_EMAIL ? () => setScreen(SCREENS.SUPER_ADMIN) : undefined} onAccount={() => setScreen(SCREENS.ACCOUNT)} onClubsChanged={loadUserClubs} onClubAdmin={canManageClubs ? () => setScreen(SCREENS.CLUB_ADMIN) : undefined} isClubOwner={canManageClubs} clubOwnerTier={clubOwnerTier} onClubPricing={!isNativeApp ? () => setScreen(SCREENS.CLUB_PRICING) : undefined} />
      )}
      {screen === SCREENS.ACCOUNT && (
        <Account onToast={triggerToast} />
      )}
      {screen === SCREENS.EVENTS && (
        <Suspense fallback={null}>
          <EventsTab
            userClubs={userClubs}
            userId={user?.id}
            userEmail={userEmail}
            onSelectEvent={(eventId) => { setSelectedEventId(eventId); setScreen(SCREENS.EVENT_DETAIL); }}
            onBack={() => setScreen(SCREENS.DASHBOARD)}
            onClubsChanged={loadUserClubs}
          />
        </Suspense>
      )}
      {screen === SCREENS.EVENT_DETAIL && selectedEventId && (
        <Suspense fallback={null}>
          <EventDetail
            eventId={selectedEventId}
            userId={user?.id}
            userEmail={userEmail}
            vehicles={vehicles}
            onBack={() => setScreen(SCREENS.EVENTS)}
            onToast={triggerToast}
            onViewRoute={(slug) => {
              setRouteSlugForViewer(slug);
              setRouteViewerReturnScreen(SCREENS.EVENT_DETAIL);
              setScreen(SCREENS.ROUTE_VIEWER);
            }}
          />
        </Suspense>
      )}
      {screen === SCREENS.JOIN_CLUB && (
        <Suspense fallback={null}>
          <JoinClub
            slug={joinClubSlug}
            userId={user?.id}
            onJoined={() => { loadUserClubs(); setScreen(SCREENS.EVENTS); }}
            onBack={() => setScreen(SCREENS.DASHBOARD)}
            onToast={triggerToast}
          />
        </Suspense>
      )}
      {screen === SCREENS.CLUB_ADMIN && canManageClubs && (
        <Suspense fallback={
          <div className="screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading Club Admin...</p>
            </div>
          </div>
        }>
          <ClubAdmin
            userId={user?.id}
            userEmail={user?.email}
            userClubs={userClubs.filter(c => c.user_role === 'owner' || c.user_role === 'admin')}
            onBack={() => setScreen(SCREENS.DASHBOARD)}
            onToast={triggerToast}
            onClubsChanged={loadUserClubs}
            clubOwnerTier={clubOwnerTier}
            onCreateRoute={() => {
              setSelectedRouteForEdit(null);
              setRouteBuilderReturnScreen(SCREENS.CLUB_ADMIN);
              setScreen(SCREENS.ROUTE_BUILDER);
            }}
            onEditRoute={(route) => {
              setSelectedRouteForEdit(route);
              setRouteBuilderReturnScreen(SCREENS.CLUB_ADMIN);
              setScreen(SCREENS.ROUTE_BUILDER);
            }}
            onViewRoute={(route) => {
              setRouteSlugForViewer(route.slug);
              setRouteViewerReturnScreen(SCREENS.CLUB_ADMIN);
              setScreen(SCREENS.ROUTE_VIEWER);
            }}
          />
        </Suspense>
      )}
      {screen === SCREENS.CLUB_PRICING && (
        <Suspense fallback={null}>
          <ClubPricing
            onBack={() => setScreen(SCREENS.SETTINGS)}
            onToast={triggerToast}
            clubOwnerTier={clubOwnerTier}
            userEmail={user?.email}
            userId={user?.id}
          />
        </Suspense>
      )}
      {screen === SCREENS.REFERRALS && (
        <Referrals
          onBack={() => setScreen(SCREENS.VEHICLE_LIST)}
          user={user}
          onToast={triggerToast}
        />
      )}
      {screen === SCREENS.ROUTES && (
        <Suspense fallback={null}>
          {(() => {
            try {
              const settings = getSettings();
              const isFreePlan = settings.subscription?.plan === 'free' && !isClubOwner;
              if (isFreePlan) {
                return (
                  <RoutesLanding
                    onUpgrade={() => setShowUpgradeModal(true)}
                    onClubPricing={() => setScreen(SCREENS.CLUB_PRICING)}
                  />
                );
              }
            } catch (err) {
              if (import.meta.env.DEV) console.error('[Routes] getSettings error:', err);
            }
            return (
              <RouteList
                userId={user?.id}
                onCreateRoute={() => {
                  setSelectedRouteForEdit(null);
                  setRouteBuilderReturnScreen(SCREENS.ROUTES);
                  setScreen(SCREENS.ROUTE_BUILDER);
                }}
                onEditRoute={(route) => {
                  setSelectedRouteForEdit(route);
                  setRouteBuilderReturnScreen(SCREENS.ROUTES);
                  setScreen(SCREENS.ROUTE_BUILDER);
                }}
                onViewRoute={(route) => {
                  setRouteSlugForViewer(route.slug);
                  setRouteViewerReturnScreen(SCREENS.ROUTES);
                  setScreen(SCREENS.ROUTE_VIEWER);
                }}
                onToast={triggerToast}
                isClubAdmin={isClubOwner}
              />
            );
          })()}
        </Suspense>
      )}
      {screen === SCREENS.ROUTE_BUILDER && (
        <Suspense fallback={null}>
          <RouteBuilder
            route={selectedRouteForEdit}
            onNavigateBack={() => {
              const returnTo = routeBuilderReturnScreen || SCREENS.ROUTES;
              setSelectedRouteForEdit(null);
              setRouteBuilderReturnScreen(null);
              setScreen(returnTo);
            }}
            onToast={triggerToast}
            clubId={userClubs.find(c => c.user_role === 'owner')?.club_id || null}
            isClubAdmin={isClubOwner}
            userClubs={userClubs.filter(c => c.user_role === 'owner')}
          />
        </Suspense>
      )}
      {screen === SCREENS.ROUTE_VIEWER && routeSlugForViewer && (
        <Suspense fallback={null}>
          <div className="route-fullscreen">
            <RouteViewer
              slug={routeSlugForViewer}
              soloMode={routeViewerReturnScreen === SCREENS.ROUTES}
              onBack={() => {
                setRouteSlugForViewer(null);
                setScreen(routeViewerReturnScreen || SCREENS.EVENT_DETAIL);
                setRouteViewerReturnScreen(null);
              }}
              onEditRoute={(route) => {
                setRouteSlugForViewer(null);
                setSelectedRouteForEdit(route);
                setRouteBuilderReturnScreen(SCREENS.ROUTES);
                setRouteViewerReturnScreen(null);
                setScreen(SCREENS.ROUTE_BUILDER);
              }}
            />
          </div>
        </Suspense>
      )}
      {screen === SCREENS.ADMIN && user?.email === ADMIN_EMAIL && (
        <Suspense fallback={
          <div className="screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading admin dashboard...</p>
            </div>
          </div>
        }>
          <AdminDashboard onBack={() => setScreen(SCREENS.DASHBOARD)} />
        </Suspense>
      )}
      {screen === SCREENS.FUN_ROUTES && (
        <Suspense fallback={null}>
          <FunRouteExplorer
            onBack={() => setScreen(SCREENS.ROUTES)}
            onToast={triggerToast}
          />
        </Suspense>
      )}
      {screen === SCREENS.ANNOUNCEMENT && announcementIntent && (
        <Suspense fallback={<div style={{ minHeight: '100dvh', background: 'var(--bg-primary)' }} />}>
          <AnnouncementViewer
            announcementId={announcementIntent}
            onBack={() => { setAnnouncementIntent(''); setScreen(SCREENS.DASHBOARD); }}
          />
        </Suspense>
      )}
      {screen === SCREENS.SUPER_ADMIN && user?.email === ADMIN_EMAIL && (
        <Suspense fallback={
          <div className="screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading super admin...</p>
            </div>
          </div>
        }>
          <SuperAdmin onBack={() => setScreen(SCREENS.SETTINGS)} />
        </Suspense>
      )}
      </main>
      {showNav && (() => {
        const hiddenTabs = [];
        if (demoVariant === 'alex') { hiddenTabs.push('events', 'routes'); }
        if (isEventsBlacklisted) { hiddenTabs.push('events'); }
        return <BottomNav activeTab={getActiveTab()} onChangeTab={handleNavTab} pendingInvitationCount={pendingInvitationCount} hiddenTabs={hiddenTabs} showClubAdmin={!isNativeApp && canManageClubs} />;
      })()}
      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} onToast={triggerToast} />
      )}
      <Toast message={toastMessage} visible={showToast} onHide={() => setShowToast(false)} />
    </>
  );
}

export default App;
